import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoHash } from 'react-icons/go';
import { CiUser } from 'react-icons/ci';
import { FaRegFileAlt } from 'react-icons/fa';
import { MdOutlineFeaturedPlayList, MdOutlineWorkOutline, MdRefresh } from 'react-icons/md';
import { PiRepeatFill } from 'react-icons/pi';
import { AiOutlineClockCircle } from 'react-icons/ai';
import { TbEdit, TbProgress } from 'react-icons/tb';
import { IoCheckmarkDoneCircleOutline, IoClose } from 'react-icons/io5';
import { LiaNotesMedicalSolid } from 'react-icons/lia';
import { BiReceipt } from 'react-icons/bi';
import ImageViewer from '../components/ui/ImageViewer';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import Button from '../components/ui/Button/Button';
import { RxDownload } from 'react-icons/rx';
import TableSearchFilter from '../components/utils/TableSearchFilter';
import ResponsiveTable from '../components/Table/ResponsiveTable';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { TableSkeleton } from '../components/Table/TableSkeleton';
import { useProjectData } from '../hooks/useProjectData';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { useCustomerData } from '../hooks/useCustomerData';
import {
  FiPlus,
  FiFileText,
  FiClock,
  FiChevronDown,
  FiChevronUp,
} from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import PopupWrapper from '../components/utils/PopupWrapper';
import { usePopup } from '../hooks/usePopup';
import InputField from '../components/ui/InputField';
import { RiFileTextLine } from 'react-icons/ri';
import { BsListCheck, BsCurrencyRupee } from 'react-icons/bs';
import ProjectFilterSlider from '../components/utils/ProjectFilterSlider';
import {
  fetchProjectsByStatus,
  loadProjects,
  searchProjects,
  fetchOriginProject,
  fetchReopenedProject,
} from '../redux/actions/projectAction';
import { clearCache, clearStatusData, clearProjectsCache, clearOriginProject, clearReopenedProject } from '../redux/slices/projectSlice';
import axiosInstance from '../axios';
import FilterPanel from '../components/utils/FilterPanel';
import TaskAssignmentTable from '../components/Projects/TaskAssignmentTable';
import { extractCustomerName, getCustomerName } from '../utils/customerUtils';
import { toast } from 'react-hot-toast';
import {
  hasProjectEditPermission,
  hasStatusPermission,
  normalizeStatus,
} from '../utils/permissionUtils';

// New Skeleton component for table rows
const SkeletonRow = () => (
  <div className="animate-pulse flex justify-between items-center py-4 border-b border-gray-200">
    <div className="w-[5%] h-4 bg-gray-200 rounded"></div>
    <div className="w-[20%] h-4 bg-gray-200 rounded"></div>
    <div className="w-[10%] h-4 bg-gray-200 rounded"></div>
    <div className="w-[25%] h-4 bg-gray-200 rounded"></div>
    <div className="w-[10%] h-4 bg-gray-200 rounded"></div>
    <div className="w-[15%] h-4 bg-gray-200 rounded"></div>
    <div className="w-[10%] h-6 bg-gray-200 rounded-full"></div>
  </div>
);

// Skeleton table component
const SkeletonTable = ({ rows = 5 }) => (
  <div className="w-full bg-white rounded-lg shadow-sm">
    <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
      <div className="w-full flex gap-8">
        {/* Column headers skeleton */}
        <div className="w-[5%] h-5 bg-gray-200 rounded"></div>
        <div className="w-[20%] h-5 bg-gray-200 rounded"></div>
        <div className="w-[10%] h-5 bg-gray-200 rounded"></div>
        <div className="w-[25%] h-5 bg-gray-200 rounded"></div>
        <div className="w-[10%] h-5 bg-gray-200 rounded"></div>
        <div className="w-[15%] h-5 bg-gray-200 rounded"></div>
        <div className="w-[10%] h-5 bg-gray-200 rounded"></div>
      </div>
    </div>
    <div className="p-4">
      {/* Generate skeleton rows */}
      {Array(rows)
        .fill(0)
        .map((_, index) => (
          <SkeletonRow key={index} />
        ))}
    </div>
    {/* Pagination skeleton */}
    <div className="p-4 flex justify-between items-center border-t border-gray-200">
      <div className="w-[15%] h-8 bg-gray-200 rounded"></div>
      <div className="flex gap-2">
        {Array(4)
          .fill(0)
          .map((_, index) => (
            <div key={index} className="w-8 h-8 bg-gray-200 rounded"></div>
          ))}
      </div>
    </div>
  </div>
);

const AllProjects: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  // Use our custom hook to ensure data is loaded
  const {
    projects: reduxProjects,
    loading: reduxLoading,
    error: reduxError,
    pagination: reduxPagination,
  } = useProjectData();

  // Get customers from Redux store with our custom hook to ensure data is loaded
  const { customers, isLoading: customersLoading } = useCustomerData();
  // Get access to filtered projects and projects loading state
  const { filteredProjects, loading: projectsLoading } = useAppSelector(state => state.projects);
  // Get user data and permissions from auth state
  const { user } = useAppSelector(state => state.auth);

  // Check if user has project permissions
  const [hasProjectPermission, setHasProjectPermission] = useState(false);

  // Add pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [displayData, setDisplayData] = useState<any[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Add a state to track initial data loading
  const [initialLoading, setInitialLoading] = useState(true);

  // Add ref to track if we've already processed Redux data to prevent re-processing
  const hasProcessedDataRef = useRef(false);

  // Add ref to track if this is the initial component mount
  const isInitialMountRef = useRef(true);

  // Add ref to track if user has actually changed filters (vs initial mount)
  const hasUserChangedFiltersRef = useRef(false);

  // Add flag to track if "All" has been explicitly clicked by user
  const hasUserClickedAllRef = useRef(false);

  // Track if dashboard status filter is being processed to prevent duplicate API calls
  const isDashboardFilterProcessingRef = useRef(false);

  // Track if an API call is already in progress to prevent duplicate calls
  const isApiCallInProgressRef = useRef(false);

  // Update initialLoading to consider customer data loading state as well
  useEffect(() => {
    // Only set initialLoading to false when Redux loading is done AND we have processed the data
    if (!reduxLoading && !customersLoading) {
      setInitialLoading(false);
    }
  }, [reduxLoading, customersLoading]);

  // Separate effect to manage loading state during operations
  useEffect(() => {
    // Set initialLoading to true only when we're actively loading
    if (reduxLoading || customersLoading || projectsLoading) {
      setInitialLoading(true);
    }
  }, [reduxLoading, customersLoading, projectsLoading]);

  // Add state for project popup
  const projectDetailsPopup = usePopup();
  const [selectedProject, setSelectedProject] = useState<any | null>(null);
  const [viewingOriginProject, setViewingOriginProject] = useState(false);
  const [viewingReopenedProject, setViewingReopenedProject] = useState(false);

  // Add state to manage the visibility of the search UI
  const [showSearchUI, setShowSearchUI] = useState(false);

  // Automatically trigger "All" selection on initial page load
  useEffect(() => {
    // Skip auto-trigger if we have an incoming status filter from dashboard navigation
    // or if dashboard filter is currently being processed
    if ((location.state?.statusFilter && location.state?.fromDashboard) || isDashboardFilterProcessingRef.current) {
      console.log('Skipping auto-trigger - status filter coming from Dashboard or already processing');
      return;
    }

    // Only auto-trigger on initial mount and if no status is already processing
    if (statusFilter === 'All' && !hasUserClickedAllRef.current && !filterLoading) {
      console.log('Auto-triggering "All" selection on initial page load');
      // Small delay to show skeleton briefly
      const timer = setTimeout(() => {
        handleStatusFilterChange('All');
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [location.state]); // Include location.state to re-check when navigation state changes

  // Add a dedicated loading state for filter changes
  const [filterLoading, setFilterLoading] = useState(false);

  // Just use a single status filter now
  const [statusFilter, setStatusFilter] = useState('All'); // Start with All selected but don't load data

  // Function to set the appropriate status filter based on current view
  const setCurrentStatusFilter = (filter: string) => {
    setStatusFilter(filter);
  };

  // Check for project permissions on mount
  useEffect(() => {
    if (user) {
      // Super Admin has all permissions
      if (user.designation === 'superAdmin') {
        setHasProjectPermission(true);
      } else if (user.permissions) {
        let projectReadValue = false;
        let projectWriteValue = false;
        let projectEditValue = false;

        // Check if permissions is an object with numeric keys (like {0: {...}})
        if (typeof user.permissions === 'object' && '0' in user.permissions) {
          //@ts-ignore
          projectReadValue = Boolean(user.permissions[0].projectRead);
          //@ts-ignore
          projectWriteValue = Boolean(user.permissions[0].projectWrite);
          //@ts-ignore
          projectEditValue = Boolean(user.permissions[0].projectEdit);
        }
        // Check if permissions is an array
        else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
          //@ts-ignore
          projectReadValue = Boolean(user.permissions[0].projectRead);
          //@ts-ignore
          projectWriteValue = Boolean(user.permissions[0].projectWrite);
          //@ts-ignore
          projectEditValue = Boolean(user.permissions[0].projectEdit);
        }
        // Direct object access if neither of the above
        else if (typeof user.permissions === 'object') {
          //@ts-ignore
          projectReadValue = Boolean(user.permissions.projectRead);
          //@ts-ignore
          projectWriteValue = Boolean(user.permissions.projectWrite);
          //@ts-ignore
          projectEditValue = Boolean(user.permissions.projectEdit);
        }

        // Check if user has any project permission
        const hasAnyProjectPermission = projectReadValue || projectWriteValue || projectEditValue;
        setHasProjectPermission(hasAnyProjectPermission);
      } else {
        // No permissions object means no access
        setHasProjectPermission(false);
      }
    } else {
      // Default to no permission if user is not defined
      setHasProjectPermission(false);
    }
  }, [user]);

  // Add filter state for our FilterPanel
  const [filterValues, setFilterValues] = useState({
    customerName: '',
    articleNo: '',
    status: 'All',
    createdFrom: '',
    createdTo: '',
  });

  // Add debounce for filter inputs
  const [debouncedFilters, setDebouncedFilters] = useState(filterValues);

  // Handle filter change with debouncing
  const handleFilterChange = (field: string, value: string) => {
    setFilterValues(prev => ({
      ...prev,
      [field]: value,
    }));

    // Mark that user has changed filters (not initial mount)
    hasUserChangedFiltersRef.current = true;

    // Reset to page 1 when filter changes
    setCurrentPage(1);

    // If the status filter is changed in the filter panel,
    // update the statusFilter state so it will be in sync when the panel is closed
    if (field === 'status') {
      // Don't update the visual slider while panel is open (it's disabled)
      // but store the value so it's ready when panel closes
      console.log(`Status filter changed in panel to: ${value}`);
    }
  };

  // Create a debounced filter effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filterValues);
    }, 500); // 500ms debounce delay

    return () => clearTimeout(timer);
  }, [filterValues]);

  // Add effect to trigger search when searchValue changes with debounce
  useEffect(() => {
    // Skip on initial mount - the auto-trigger effect will handle initial data loading
    // This prevents duplicate API calls when the page first loads
    if (isInitialMountRef.current) {
      console.log('Skipping searchValue effect on initial mount');
      return;
    }

    // Only trigger search if we actually have a search value
    if (searchValue && searchValue.trim() !== '') {
      const timer = setTimeout(() => {
        handleSearch(searchValue.trim());
      }, 300);
      return () => clearTimeout(timer);
    } else if (searchValue === '') {
      // If search is cleared, reload projects based on current status filter
      setCurrentPage(1);

      // Immediately clear display data and show loading
      setDisplayData([]);
      setFilterLoading(true);

      // IMPORTANT: Clear ALL cached data from Redux and localStorage first
      dispatch(clearCache());
      dispatch(clearStatusData());
      dispatch(clearProjectsCache());

      // Reset the processed data flag to allow fresh data processing
      hasProcessedDataRef.current = false;

      // Wait for the clearing to complete, then load fresh data
      setTimeout(() => {
        console.log('Search cleared - starting fresh data load after cache clear');

        // Determine which API call to make based on current status filter
        if (statusFilter === 'All' && hasUserClickedAllRef.current) {
          console.log('Loading all projects after search clear');
          // Load all projects - force fresh API call
          dispatch(
            loadProjects({
              page: 1,
              limit: itemsPerPage,
            })
          )
            .unwrap()
            .then(result => {
              console.log('Search cleared - reloaded all projects:', result);
              if (result && Array.isArray(result.data)) {
                const formattedProjects = formatProjectsForDisplay(result.data);
                setDisplayData(formattedProjects);
                if (result.pagination) {
                  setTotalItems(result.pagination.total);
                  setTotalPages(result.pagination.pages);
                }
              } else {
                setDisplayData([]);
                setTotalItems(0);
                setTotalPages(1);
              }
              setFilterLoading(false);
            })
            .catch(error => {
              console.error('Error reloading all projects after search clear:', error);
              setFilterLoading(false);
            });
        } else if (statusFilter && statusFilter !== 'All') {
          console.log(`Loading projects with status ${statusFilter} after search clear`);
          // Load projects with specific status filter
          const apiStatus = convertUIStatusToApiStatus(statusFilter);
          dispatch(
            fetchProjectsByStatus({
              status: apiStatus,
              page: 1,
              limit: itemsPerPage,
            })
          )
            .unwrap()
            .then(result => {
              console.log(
                `Search cleared - reloaded projects with status ${statusFilter}:`,
                result
              );
              if (result && Array.isArray(result.data)) {
                const formattedProjects = formatProjectsForDisplay(result.data);
                setDisplayData(formattedProjects);
                if (result.pagination) {
                  setTotalItems(result.pagination.total);
                  setTotalPages(result.pagination.pages);
                }
              } else {
                setDisplayData([]);
                setTotalItems(0);
                setTotalPages(1);
              }
              setFilterLoading(false);
            })
            .catch(error => {
              console.error(
                `Error reloading projects with status ${statusFilter} after search clear:`,
                error
              );
              setFilterLoading(false);
            });
        } else {
          console.log('No status filter selected after search clear');
          // No status filter selected, just clear loading state
          setFilterLoading(false);
        }
      }, 100); // Small delay to ensure clearing operations complete
    }
    // Note: statusFilter is intentionally NOT in dependencies - handleStatusFilterChange handles filter-based fetching
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  // New effect: Handle filter changes with API calls
  useEffect(() => {
    // Only make API calls if user has changed filters and panel is open
    if (!showFilterPanel || !hasUserChangedFiltersRef.current) return;

    // Check if any filter has a value (excluding status 'All')
    const hasActiveFilters =
      (debouncedFilters.customerName && debouncedFilters.customerName.trim() !== '') ||
      (debouncedFilters.articleNo && debouncedFilters.articleNo.trim() !== '') ||
      (debouncedFilters.createdFrom && debouncedFilters.createdFrom.trim() !== '') ||
      (debouncedFilters.createdTo && debouncedFilters.createdTo.trim() !== '') ||
      (debouncedFilters.status && debouncedFilters.status !== 'All');

    console.log('Filter change detected:', debouncedFilters);
    console.log('Has active filters:', hasActiveFilters);

    const makeFilteredAPICall = async () => {
      setFilterLoading(true);
      setCurrentPage(1); // Reset to first page when filters change

      // Reset the processed flag so new data will be processed
      hasProcessedDataRef.current = false;
      console.log('Reset hasProcessedDataRef to allow new filter data processing');

      try {
        let result;

        // Determine which API call to make based on status and other filters
        if (debouncedFilters.status && debouncedFilters.status !== 'All') {
          // Use fetchProjectsByStatus when specific status is selected
          const apiStatus = convertUIStatusToApiStatus(debouncedFilters.status);
          console.log(`Making filtered API call with status: ${apiStatus}`);

          result = await dispatch(
            fetchProjectsByStatus({
              status: apiStatus,
              page: 1,
              limit: itemsPerPage,
              search: searchValue && searchValue.trim() !== '' ? searchValue.trim() : undefined,
              customerName:
                debouncedFilters.customerName && debouncedFilters.customerName.trim() !== ''
                  ? debouncedFilters.customerName.trim()
                  : undefined,
              articleNo:
                debouncedFilters.articleNo && debouncedFilters.articleNo.trim() !== ''
                  ? debouncedFilters.articleNo.trim()
                  : undefined,
              createdFrom:
                debouncedFilters.createdFrom && debouncedFilters.createdFrom.trim() !== ''
                  ? debouncedFilters.createdFrom.trim()
                  : undefined,
              createdTo:
                debouncedFilters.createdTo && debouncedFilters.createdTo.trim() !== ''
                  ? debouncedFilters.createdTo.trim()
                  : undefined,
            })
          ).unwrap();
        } else {
          // Use loadProjects for 'All' status or when no status is selected
          console.log('Making filtered API call for all projects');

          result = await dispatch(
            loadProjects({
              page: 1,
              limit: itemsPerPage,
              search: searchValue && searchValue.trim() !== '' ? searchValue.trim() : undefined,
              customerName:
                debouncedFilters.customerName && debouncedFilters.customerName.trim() !== ''
                  ? debouncedFilters.customerName.trim()
                  : undefined,
              articleNo:
                debouncedFilters.articleNo && debouncedFilters.articleNo.trim() !== ''
                  ? debouncedFilters.articleNo.trim()
                  : undefined,
              createdFrom:
                debouncedFilters.createdFrom && debouncedFilters.createdFrom.trim() !== ''
                  ? debouncedFilters.createdFrom.trim()
                  : undefined,
              createdTo:
                debouncedFilters.createdTo && debouncedFilters.createdTo.trim() !== ''
                  ? debouncedFilters.createdTo.trim()
                  : undefined,
            })
          ).unwrap();
        }

        console.log('Filter API call result:', result);

        // The Redux state will be automatically updated by the reducers
        // The formatProjectsForDisplay will be called through the existing useEffect that watches reduxProjects
      } catch (error) {
        console.error('Error making filtered API call:', error);
        toast.error('Failed to apply filters');
      } finally {
        setFilterLoading(false);
      }
    };

    // Make the API call
    makeFilteredAPICall();
  }, [debouncedFilters, dispatch, itemsPerPage, searchValue, showFilterPanel]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Removed dropdown functionality
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Helper function to filter projects on client-side
  const filterProjectsClientSide = (projects: any[]) => {
    if (!projects || projects.length === 0) return [];

    let filtered = [...projects];

    // Apply search filter if exists
    if (searchValue && searchValue.trim() !== '') {
      const searchLower = searchValue.toLowerCase();
      filtered = filtered.filter(
        project =>
          project.projectName?.toLowerCase() === searchLower ||
          project.projectNumber?.toLowerCase() === searchLower ||
          project.customerName?.toLowerCase() === searchLower
      );
    }

    // Add other filter logic here as needed
    // (customerName, articleNo, date ranges, etc.)

    return filtered;
  };

  // Function to apply filters (simplified - mainly for external triggers like reset)
  const applyFilters = useCallback(async () => {
    console.log('applyFilters called - filters handled by dedicated useEffect');
    // The actual filter logic is now handled by the dedicated useEffect above
    // This function remains for compatibility with any external calls
  }, []);

  // Helper function to format dates in DD-MM-YY format
  const formatDateToDDMMYY = (dateInput: string | Date): string => {
    const date = new Date(dateInput);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(2);
    return `${day}-${month}-${year}`;
  };

  // Helper function to extract time in HH:MM AM/PM format from date string
  const extractTimeFromDate = (dateInput: string | Date | null | undefined): string => {
    if (!dateInput) return '';
    const date = new Date(dateInput);
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  // Helper function to highlight text matches
  const highlightMatch = (text: string | number, query: string) => {
    if (!query.trim() || !text) return text;

    const textStr = String(text);
    const regex = new RegExp(`(${query.trim()})`, 'gi');
    const parts = textStr.split(regex);

    return (
      <>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <span key={i} className="bg-yellow-200 font-medium">
              {part}
            </span>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </>
    );
  };

  // Create a helper function to convert UI status values to API status values
  const convertUIStatusToApiStatus = (status: string): string => {
    if (!status || status === 'All') return '';

    // Already using proper format (e.g., IN_PROGRESS, HOLD)
    if (status.includes('_')) return status;

    // Convert from display format to API format
    switch (status) {
      case 'In Progress':
        return 'IN_PROGRESS';
      case 'On Hold':
        return 'HOLD';
      case 'Payment Received':
        return 'PAYMENT_RECEIVED';
      case 'Non Active':
        return 'NON_ACTIVE';
      case 'Unpaid Reopen':
        return 'REVISION';
      case 'Paid Reopen':
        return 'REOPEN';
      default:
        // Convert other status values: "Status Name" -> "STATUS_NAME"
        return status.toUpperCase().replace(/ /g, '_');
    }
  };

  // Handle status filter change
  const handleStatusFilterChange = (filter: string) => {
    // Debug log
    console.log(`Filter clicked: ${filter}`);

    // Skip if an API call is already in progress to prevent duplicates
    if (isApiCallInProgressRef.current) {
      console.log('Skipping filter change - API call already in progress');
      return;
    }

    // Mark API call as in progress
    isApiCallInProgressRef.current = true;

    // Mark that user has changed filters (not initial mount)
    hasUserChangedFiltersRef.current = true;

    // Track if user explicitly clicked "All"
    if (filter === 'All') {
      hasUserClickedAllRef.current = true;
    }

    // Immediately clear all current data and show loading state
    dispatch(clearStatusData());

    // Clear local display data to ensure no stale data is shown
    setDisplayData([]);

    // Reset the processed data flag to allow new data processing
    hasProcessedDataRef.current = false;

    // Mark that this is no longer initial mount (user-initiated action)
    isInitialMountRef.current = false;

    // Set local loading state as well
    setFilterLoading(true);

    // Update the status filter state
    setStatusFilter(filter);

    // Always reset to the first page when changing filters
    setCurrentPage(1);

    // If the filter is "All", only load data if user has explicitly clicked "All"
    if (filter === 'All' && hasUserClickedAllRef.current) {
      // Fetch all projects through Redux with pagination
      dispatch(
        loadProjects({
          page: 1,
          limit: itemsPerPage,
          search: searchValue && searchValue.trim() !== '' ? searchValue.trim() : undefined,
        })
      )
        .unwrap()
        .then(result => {
          console.log('All projects loaded via Redux:', result);

          // Process the data (including empty arrays)
          if (result && Array.isArray(result.data)) {
            const formattedProjects = formatProjectsForDisplay(result.data);
            setDisplayData(formattedProjects);

            // Update pagination data
            if (result.pagination) {
              setTotalItems(result.pagination.total);
              setTotalPages(result.pagination.pages);
            }
          } else {
            // If no data property or invalid format, set empty display
            setDisplayData([]);
            setTotalItems(0);
            setTotalPages(1);
          }
          setFilterLoading(false);
          isApiCallInProgressRef.current = false;
        })
        .catch(error => {
          console.error('Error loading all projects:', error);
          setFilterLoading(false);
          isApiCallInProgressRef.current = false;
        });
      return;
    }

    // For specific status filters, use fetchProjectsByStatus
    const apiFilter = convertUIStatusToApiStatus(filter);
    console.log(`Converted status filter: ${filter} -> ${apiFilter}`);

    // Use Redux action to fetch filtered projects
    dispatch(
      fetchProjectsByStatus({
        status: apiFilter,
        page: 1,
        limit: itemsPerPage,
        search: searchValue && searchValue.trim() !== '' ? searchValue.trim() : undefined,
      })
    )
      .unwrap()
      .then(result => {
        console.log(`Projects with status ${filter} loaded via Redux:`, result);

        // Process the data (including empty arrays)
        if (result && Array.isArray(result.data)) {
          const formattedProjects = formatProjectsForDisplay(result.data);
          setDisplayData(formattedProjects);

          // Update pagination data
          if (result.pagination) {
            setTotalItems(result.pagination.total);
            setTotalPages(result.pagination.pages);
          }
        } else {
          // If no data property or invalid format, set empty display
          setDisplayData([]);
          setTotalItems(0);
          setTotalPages(1);
        }
        setFilterLoading(false);
        isApiCallInProgressRef.current = false;
      })
      .catch(error => {
        console.error(`Error fetching projects with status ${filter}:`, error);
        setFilterLoading(false);
        isApiCallInProgressRef.current = false;
      });
  };

  // Helper function to filter projects to only show current user's projects
  const filterMyProjects = (projects: any[]): any[] => {
    if (!user?.id) return projects;

    return projects.filter(project => {
      // Check if current user is assigned to this project
      if (project.projectAssigned && project.projectAssigned.length > 0) {
        // Get the latest assignment (index 0)
        const latestAssignment = project.projectAssigned[0];

        // Check if current user matches either adminId or employeeId in the latest assignment
        return (
          (latestAssignment.adminId && latestAssignment.adminId === user.id) ||
          (latestAssignment.employeeId && latestAssignment.employeeId === user.id) ||
          latestAssignment.assignedToEmployee === user.id
        );
      }

      // Also check assignedEmployees array as fallback
      if (project.assignedEmployees && project.assignedEmployees.length > 0) {
        return project.assignedEmployees.some(
          assignment =>
            (assignment.adminId && assignment.adminId === user.id) ||
            (assignment.employeeId && assignment.employeeId === user.id) ||
            assignment.assignedToEmployee === user.id
        );
      }

      // Check direct assignment fields as fallback
      if (project.assignedEmployeeId === user.id) {
        return true;
      }

      // If no assignments found, exclude from "My Projects"
      return false;
    });
  };

  // Helper function to get the latest status from a project's assignments
  const getLatestProjectStatus = (project: any): string => {
    // If no project or it's not an object, return empty string
    if (!project || typeof project !== 'object') {
      console.warn('Invalid project object passed to getLatestProjectStatus');
      return '';
    }

    // If project has a direct status property and no assignments, use that
    if (!project.projectAssigned || project.projectAssigned.length === 0) {
      return project.status || '';
    }

    // Make a safe copy of assignments to avoid mutation errors
    const assignments = Array.isArray(project.projectAssigned) ? [...project.projectAssigned] : [];

    if (assignments.length === 0) {
      return project.status || '';
    }

    // Sort assignments by date (newest first)
    const sortedAssignments = assignments.sort((a, b) => {
      // Make sure we handle potential null/undefined dates
      const dateA = a?.assignedDate || a?.assignedDateAndTime;
      const dateB = b?.assignedDate || b?.assignedDateAndTime;

      if (!dateA) return 1; // a is older (or invalid date)
      if (!dateB) return -1; // b is older (or invalid date)

      // Convert to timestamps for comparison
      const timeA = new Date(dateA).getTime();
      const timeB = new Date(dateB).getTime();

      // Sort descending (newest first)
      return timeB - timeA;
    });

    // For debugging
    console.log('Project ID:', project.id, 'Latest assignment:', sortedAssignments[0]);

    // Return the status of the most recent assignment
    return sortedAssignments[0]?.status || project.status || '';
  };

  // Helper function to process and apply filter to projects data
  const applyFilterAndSetDisplayData = (
    projects: any[],
    filter: string,
    page: number,
    perPage: number
  ) => {
    try {
      // Use the projects data passed directly to this function
      let projectsToDisplay = [...projects];

      // Apply search filter if search value exists - enhanced dynamic search
      if (searchValue.trim()) {
        const searchLower = searchValue.toLowerCase();
        projectsToDisplay = projectsToDisplay.filter(
          project =>
            // Exact match in key fields (case-insensitive)
            project.projectName?.toLowerCase() === searchLower ||
            project.projectNumber?.toLowerCase() === searchLower ||
            (project.manakProjectId &&
              project.manakProjectId.toString().toLowerCase() === searchLower) ||
            // Try to match customer name if possible
            (project.ClientId &&
              customers.find(c => c.id === project.ClientId)?.companyName.toLowerCase() ===
              searchLower)
        );
      }

      // Apply status filter if not set to 'All'
      if (filter !== 'All') {
        console.log(`Filtering for status: ${filter}`);
        projectsToDisplay = projectsToDisplay.filter(project => {
          // Get the latest status from the project
          const latestStatus = getLatestProjectStatus(project);

          // Convert the API status to display format for comparison
          const latestDisplayStatus = formatStatusText(latestStatus);

          // Log for debugging
          console.log(
            `Project ${project.projectName || project.id}: Latest status = ${latestStatus}, Display status = ${latestDisplayStatus}, Filter = ${filter}`
          );

          // Compare the formatted display status with the filter
          return latestDisplayStatus === filter;
        });

        console.log(`After status filtering: ${projectsToDisplay.length} projects remaining`);
      } // Convert projects to the format expected by our table
      const formattedProjects = projectsToDisplay.map((project, index) => {
        // Get the latest status
        const latestStatus = getLatestProjectStatus(project);
        console.log(`Project ${project.projectName} status: ${latestStatus}`);

        // Get customer name from customers Redux state - Improved customer name extraction
        let customerName = project.customerName;
        if (project.ClientId && customers.length > 0) {
          // Always look up customer from Redux store to ensure we have the latest data
          const matchingCustomer = customers.find(c => c.id === project.ClientId);
          if (matchingCustomer) {
            customerName = matchingCustomer.companyName;
          }
        }

        // Ensure we don't show "Client #ID" fallback
        if (!customerName || customerName.startsWith('Client #')) {
          customerName = extractCustomerName(project, customers);
        }

        return {
          // Always use manakProjectId if available to ensure consistency
          id: project.manakProjectId ? `${project.manakProjectId}` : `Project-${index + 1}`,
          status: formatStatusText(latestStatus),
          customerName: customerName,
          articleNo: project.projectNumber || `NT-${3015 + index}`,
          projectName: project.projectName || `Project ${index + 1}`,
          createdOn: {
            date: project.createdAt
              ? formatDateToDDMMYY(project.createdAt)
              : formatDateToDDMMYY(new Date()),
            time: extractTimeFromDate(project.createdAt),
          },
          // Include additional data as needed
          rawProject: project, // Include the raw project data for reference if needed
        };
      });

      // Apply pagination manually for Redux data
      const startIndex = (page - 1) * perPage;
      const endIndex = startIndex + perPage;
      const paginatedData = formattedProjects.slice(startIndex, endIndex);

      // Set the display data and total items for pagination
      setDisplayData(paginatedData);
      setTotalItems(formattedProjects.length);

      // Once we have data, set initialLoading to false
      setInitialLoading(false);
    } catch (error) {
      console.error('Error processing projects in applyFilterAndSetDisplayData:', error);
      setDisplayData([]);
      setTotalItems(0);
      setInitialLoading(false);
    }
  };

  // Table column definitions
  const columns = [
    {
      key: 'id',
      header: 'M ID',
      icon: <GoHash />,
      width: '7%',
      headerClassName: 'font-bold text-sm',
      truncate: true,
      renderCell: (value, row) => (
        <div className="flex items-center">
          <span className="flex-shrink-0">{value}</span>
          {row.rawProject?.projectRevisions && row.rawProject.projectRevisions.length > 0 ? (
            <span className="ml-2 bg-purple-500 text-white text-xs font-bold rounded-full px-2 py-0.5 flex-shrink-0">
              {row.rawProject.projectRevisions[0].formattedRevisionNumber ||
                `R${row.rawProject.projectRevisions[0].revisionNumber}`}
            </span>
          ) : row.rawProject?.revisionNumber > 0 ? (
            <span className="ml-2 bg-purple-500 text-white text-xs font-bold rounded-full px-2 py-0.5 flex-shrink-0">
              R{row.rawProject.revisionNumber}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'customerName',
      header: 'Company Name',
      icon: <CiUser />,
      width: '18%',
      headerClassName: 'font-bold text-sm',
      truncate: true,
    },
    {
      key: 'articleNo',
      header: 'Article No',
      icon: <FaRegFileAlt />,
      width: '12%',
      headerClassName: 'font-bold text-sm',
    },
    {
      key: 'projectName',
      header: 'Project Name',
      icon: <MdOutlineFeaturedPlayList />,
      width: '18%',
      headerClassName: 'font-bold text-sm',
      truncate: true,
    },
    {
      key: 'assignedTo',
      header: 'Assigned To',
      icon: <MdOutlineWorkOutline />,
      width: '15%',
      headerClassName: 'font-bold text-sm',
      truncate: true,
      renderCell: (_, row) => {
        const currentUserId = currentUser?.id;

        // Use the assignedEmployeeName directly from API if available
        if (row.rawProject?.assignedEmployeeName) {
          const isSelfAssigned = row.rawProject.isSelfAssigned;

          // First check if this is the current user (either by self-assignment or by direct match)
          if (
            (isSelfAssigned && currentUserId === row.rawProject.assignedToEmployee) ||
            row.rawProject.assignedEmployeeId === currentUserId ||
            row.rawProject.assignedToEmployee === currentUserId
          ) {
            return (
              <div className="flex items-center">
                <span className="bg-lime-100 text-lime-800 px-2 py-1 rounded-full font-medium flex items-center">
                  <span className="mr-1">👤</span> You
                </span>
              </div>
            );
          }

          // If it's marked as self-assigned but not the current user, it's an admin
          if (isSelfAssigned) {
            return (
              <div className="flex items-center">
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-medium flex items-center">
                  <span className="mr-1">👑</span> {row.rawProject.assignedEmployeeName}
                </span>
              </div>
            );
          }

          return (
            <div className="flex items-center">
              <span className="text-gray-800">{row.rawProject.assignedEmployeeName}</span>
            </div>
          );
        }

        // Fallback to the old method if direct name is not available
        if (row.rawProject?.projectAssigned && row.rawProject.projectAssigned.length > 0) {
          const latestAssignment = row.rawProject.projectAssigned[0];

          // Check if this assignment is to the current user
          if (
            (latestAssignment.adminId && latestAssignment.adminId === currentUserId) ||
            (latestAssignment.employeeId && latestAssignment.employeeId === currentUserId)
          ) {
            return (
              <div className="flex items-center">
                <span className="bg-lime-100 text-lime-800 px-2 py-1 rounded-full font-medium flex items-center">
                  <span className="mr-1">👤</span> You
                </span>
              </div>
            );
          }

          // Check if assignment has assignedToName (from our API update)
          if (latestAssignment.assignedToName) {
            const isSelfAssigned = latestAssignment.adminId ? true : false;

            if (isSelfAssigned) {
              return (
                <div className="flex items-center">
                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-medium flex items-center">
                    <span className="mr-1">👑</span> {latestAssignment.assignedToName}
                  </span>
                </div>
              );
            }

            return (
              <div className="flex items-center">
                <span className="text-gray-800">{latestAssignment.assignedToName}</span>
              </div>
            );
          }

          // Check if it's a self-assignment (adminId present)
          if (latestAssignment.adminId) {
            // Try to find admin name
            const admin = employees.find(e => e.id === latestAssignment.adminId);
            return (
              <div className="flex items-center">
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-medium flex items-center">
                  <span className="mr-1">👑</span> {admin?.fullname || 'Admin'}
                </span>
              </div>
            );
          }

          // Check if it's assigned to an employee
          if (latestAssignment.employeeId) {
            // Try to find employee name
            const employee = employees.find(e => e.id === latestAssignment.employeeId);
            if (employee) {
              return <span className="text-gray-800">{employee.fullname}</span>;
            } else {
              // If not found in employees, display as Admin with special styling
              return (
                <div className="flex items-center">
                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-medium flex items-center">
                    <span className="mr-1">👑</span> Admin
                  </span>
                </div>
              );
            }
          }
        }

        return <span className="text-gray-500 italic">Not assigned</span>;
      },
    },
    {
      key: 'createdOn',
      header: 'Created At',
      icon: <AiOutlineClockCircle />,
      width: '12%',
      headerClassName: 'font-bold text-sm',
    },
    {
      key: 'status',
      header: 'Status',
      icon: <PiRepeatFill />,
      width: '10%',
      headerClassName: 'font-bold text-sm',
    },
    {
      key: 'action',
      header: 'Action',
      width: '11%',
      headerClassName: 'font-bold text-sm',
      renderCell: (value, row) => {
        // Get the current project status for better error messaging
        let currentStatus = row.status;
        if (row.rawProject?.projectAssigned && row.rawProject.projectAssigned.length > 0) {
          const sortedAssignments = [...row.rawProject.projectAssigned].sort(
            (a, b) =>
              new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime() -
              new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime()
          );
          if (sortedAssignments[0] && sortedAssignments[0].status) {
            currentStatus = sortedAssignments[0].status;
          }
        }

        // Normalize the status for display
        const normalizedStatus = normalizeStatus(currentStatus);
        const displayStatus = formatStatusText(normalizedStatus);

        // Check if user has permission to edit this project based on its status
        const canEdit = canEditProjectWithStatus(row);
        const isTransferred = isProjectTransferred(row);

        // Determine button style and functionality based on permissions
        const buttonClass = canEdit
          ? 'text-green-500 border border-green-500 rounded-full px-4 py-1 flex items-center'
          : 'text-gray-400 border border-gray-400 rounded-full px-4 py-1 flex items-center cursor-not-allowed opacity-60';

        // Determine tooltip message based on why user can't edit
        let tooltipMessage = 'Edit project';

        if (!canEdit) {
          if (isTransferred) {
            tooltipMessage = "You can't edit this project as it has been reassigned to another user";
          } else {
            tooltipMessage = `You don't have permission to edit projects with ${displayStatus} status`;
          }
        }

        // Create a detailed debug message for the console
        console.log(`Action button for project ${row.id || 'unknown'}:`, {
          status: currentStatus,
          normalizedStatus,
          displayStatus,
          canEdit,
          isTransferred,
          tooltipMessage,
        });

        return (
          <button
            className="text-green-500 border border-green-500 rounded-full px-4 py-1 flex items-center"
            onClick={e => {
              e.stopPropagation(); // Prevent row click event from triggering
              handleEditProject(row);
            }}
            title="Edit project"
          >
            <TbEdit className="mr-1" /> Edit
          </button>
        );
      },
    },
  ];

  // Status colors mapping
  const statusColors = {
    // Formatted display versions
    'In Progress': 'bg-blue-100 text-blue-800',
    Delivered: 'bg-green-100 text-green-800',
    Review: 'bg-yellow-100 text-yellow-800',
    Assigned: 'bg-indigo-100 text-indigo-800',
    Billed: 'bg-red-100 text-red-800',
    Hold: 'bg-orange-100 text-orange-800',
    Cancel: 'bg-red-100 text-red-800',
    Cancelled: 'bg-red-100 text-red-800', // Fixed styling for Cancelled
    'Payment Received': 'bg-green-100 text-green-800',
    Revision: 'bg-purple-50 text-purple-700',
    Reopen: 'bg-purple-100 text-purple-800',
    Unassigned: 'bg-gray-100 text-gray-800',
    'Non Active': 'bg-gray-200 text-gray-700', // Improved styling for Non Active

    // Original backend versions (uppercase with underscores)
    IN_PROGRESS: 'bg-blue-100 text-blue-800',
    DELIVERED: 'bg-green-100 text-green-800',
    REVIEW: 'bg-yellow-100 text-yellow-800',
    ASSIGNED: 'bg-indigo-100 text-indigo-800',
    BILLED: 'bg-red-100 text-red-800',
    HOLD: 'bg-orange-100 text-orange-800',
    CANCELLED: 'bg-red-100 text-red-800',
    PAYMENT_RECEIVED: 'bg-green-100 text-green-800',
    REVISION: 'bg-purple-50 text-purple-700',
    REOPEN: 'bg-purple-100 text-purple-800',
    UNASSIGNED: 'bg-gray-100 text-gray-800',
    NON_ACTIVE: 'bg-gray-200 text-gray-700', // Improved styling for Non Active

    // Old format status values
    'For Review': 'bg-yellow-100 text-yellow-800',
    'Revision 2': 'bg-[#E2E3F5] text-[#2C2F61]',
    'On Hold': 'bg-orange-100 text-orange-800',
    ON_HOLD: 'bg-orange-100 text-orange-800',
  };

  // Status icons mapping
  const statusIcons = {
    // Formatted display versions
    'In Progress': <TbProgress className="w-4 h-4" />,
    Delivered: <IoCheckmarkDoneCircleOutline className="w-4 h-4" />,
    Review: <LiaNotesMedicalSolid className="w-4 h-4" />,
    Assigned: <BiReceipt className="w-4 h-4" />,
    Billed: <BiReceipt className="w-4 h-4" />,
    Hold: <AiOutlineClockCircle className="w-4 h-4" />,
    Cancel: <IoCheckmarkDoneCircleOutline className="w-4 h-4" />,
    Cancelled: <IoCheckmarkDoneCircleOutline className="w-4 h-4" />,
    'Payment Received': <BsCurrencyRupee className="w-4 h-4" />,
    Revision: <PiRepeatFill className="w-4 h-4" />,
    Reopen: <PiRepeatFill className="w-4 h-4" />,
    Unassigned: <MdOutlineWorkOutline className="w-4 h-4" />,
    'Non Active': <AiOutlineClockCircle className="w-4 h-4" />, // Using clock icon for expired warranty

    // Original backend versions (uppercase with underscores)
    IN_PROGRESS: <TbProgress className="w-4 h-4" />,
    DELIVERED: <IoCheckmarkDoneCircleOutline className="w-4 h-4" />,
    REVIEW: <LiaNotesMedicalSolid className="w-4 h-4" />,
    ASSIGNED: <BiReceipt className="w-4 h-4" />,
    BILLED: <BiReceipt className="w-4 h-4" />,
    HOLD: <AiOutlineClockCircle className="w-4 h-4" />,
    CANCELLED: <IoCheckmarkDoneCircleOutline className="w-4 h-4" />,
    PAYMENT_RECEIVED: <BsCurrencyRupee className="w-4 h-4" />,
    REVISION: <PiRepeatFill className="w-4 h-4" />,
    REOPEN: <PiRepeatFill className="w-4 h-4" />,
    UNASSIGNED: <MdOutlineWorkOutline className="w-4 h-4" />,
    NON_ACTIVE: <AiOutlineClockCircle className="w-4 h-4" />, // Using clock icon for expired warranty

    // Old format status values
    'In-Progress': <TbProgress className="w-4 h-4" />,
    'For Review': <LiaNotesMedicalSolid className="w-4 h-4" />,
  };

  // Add state for showing assignment history
  const [showAssignmentHistory, setShowAssignmentHistory] = useState(false);
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  const [showOriginAssignmentHistory, setShowOriginAssignmentHistory] = useState(false);
  const [showReopenedAssignmentHistory, setShowReopenedAssignmentHistory] = useState(false);
  const [showReopenedProjectImages, setShowReopenedProjectImages] = useState(false);
  const [selectedReopenedImage, setSelectedReopenedImage] = useState<string>('');

  // Get employee, designations, and user data from Redux
  const { employees, designations } = useAppSelector(state => state.users);
  const { user: currentUser } = useAppSelector(state => state.auth);
  const { originProject, originProjectLoading, originProjectError, reopenedProject, reopenedProjectLoading, reopenedProjectError } = useAppSelector(state => state.projects);

  // Helper function to format status text for display
  const formatStatusText = (status: string): string => {
    if (!status) return '';

    // Replace underscores with spaces
    let formattedStatus = status.replace(/_/g, ' ');

    // Convert to title case (capitalize first letter of each word)
    formattedStatus = formattedStatus
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    // Handle special cases
    switch (status.toUpperCase()) {
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'PAYMENT_RECEIVED':
        return 'Payment Received';
      case 'NON_ACTIVE':
        return 'Non Active';
      default:
        return formattedStatus;
    }
  };

  // Modified to properly handle assignedBy fields from backend
  const formatAssignmentHistory = (project: any, employeesList: any[], currentUserData: any) => {
    if (!project?.projectAssigned || project.projectAssigned.length === 0) {
      return [];
    }

    console.log('formatAssignmentHistory - Raw project data:', project);
    console.log('formatAssignmentHistory - Project assignments:', project.projectAssigned);

    // Sort assignments by date (newest first)
    const sortedAssignments = [...project.projectAssigned].sort((a, b) => {
      const dateA = new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime();
      const dateB = new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime();
      return dateA - dateB;
    });

    // Return the raw assignments - let the TaskAssignmentTable component handle all formatting
    return sortedAssignments;
  };

  // Format origin project data similar to selectedProject
  const formattedOriginProject = React.useMemo(() => {
    if (!originProject) return null;

    // Get the customer name for the origin project
    // Use ClientId (from database) or customerId (if mapped)
    const customerId = originProject.customerId || originProject.ClientId;
    const originCustomerName = getCustomerName(customerId, customers);

    // Get most recent status
    let currentStatus = originProject.status || 'Unassigned';
    if (originProject.projectAssigned && originProject.projectAssigned.length > 0) {
      const sortedAssignments = [...originProject.projectAssigned].sort(
        (a, b) =>
          new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime() -
          new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime()
      );
      if (sortedAssignments[0]?.status) {
        currentStatus = sortedAssignments[0].status;
      }
    }

    return {
      id: originProject.manakProjectId,
      customerName: originCustomerName,
      articleNo: originProject.projectNumber || originProject.articleNo || 'N/A',
      projectName: originProject.projectName,
      status: formatStatusText(currentStatus),
      displayStatus: formatStatusText(currentStatus),
      rawProject: originProject,
    };
  }, [originProject, customers]);

  // Format origin project assignment history
  const originAssignmentHistory = React.useMemo(() => {
    if (!originProject) return [];
    return formatAssignmentHistory(originProject, employees, currentUser);
  }, [originProject, employees, currentUser]);

  // Format reopened project data similar to selectedProject
  const formattedReopenedProject = React.useMemo(() => {
    if (!reopenedProject) return null;

    // Get the customer name for the reopened project
    // Use ClientId (from database) or customerId (if mapped)
    const customerId = reopenedProject.customerId || reopenedProject.ClientId;
    const reopenedCustomerName = getCustomerName(customerId, customers);

    // Get most recent status
    let currentStatus = reopenedProject.status || 'Unassigned';
    if (reopenedProject.projectAssigned && reopenedProject.projectAssigned.length > 0) {
      const sortedAssignments = [...reopenedProject.projectAssigned].sort(
        (a, b) =>
          new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime() -
          new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime()
      );
      if (sortedAssignments[0]?.status) {
        currentStatus = sortedAssignments[0].status;
      }
    }

    return {
      id: reopenedProject.manakProjectId,
      customerName: reopenedCustomerName,
      articleNo: reopenedProject.projectNumber || reopenedProject.articleNo || 'N/A',
      projectName: reopenedProject.projectName,
      status: formatStatusText(currentStatus),
      displayStatus: formatStatusText(currentStatus),
      rawProject: reopenedProject,
    };
  }, [reopenedProject, customers]);

  // Format reopened project assignment history
  const reopenedAssignmentHistory = React.useMemo(() => {
    if (!reopenedProject) return [];
    return formatAssignmentHistory(reopenedProject, employees, currentUser);
  }, [reopenedProject, employees, currentUser]);

  // Helper function to check if a user is an admin
  const isUserAdmin = (user: any) => {
    // Check all possible admin indicators
    return Boolean(
      user?.role === 'ADMIN' ||
      user?.userType === 'ADMIN' ||
      user?.assignedByUserType === 'ADMIN' ||
      user?.isAdmin === true ||
      user?.designation?.name === 'Super Admin' ||
      user?.isSuperAdmin === true
    );
  };

  // Helper function to get designation name from designationId
  const getDesignationName = (designationId: any, isAdmin = false) => {
    // If user is admin, return Super Admin regardless of designation
    if (isAdmin) return 'Super Admin';

    // Otherwise lookup designation by ID
    if (!designationId || !designations || designations.length === 0) return 'Staff Member';
    const selectedDesignation = designations.find(d => d.id === designationId);
    return selectedDesignation ? selectedDesignation.name : 'Staff Member';
  };

  // Helper function to check if project was transferred from current user to another user
  const isProjectTransferred = (project: any): boolean => {
    if (
      !project?.rawProject?.projectAssigned ||
      project.rawProject.projectAssigned.length < 2 ||
      !user?.id
    ) {
      return false;
    }

    // Sort assignments by date (newest first)
    const sortedAssignments = [...project.rawProject.projectAssigned].sort((a, b) => {
      const dateA = new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime();
      const dateB = new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime();
      return dateA - dateB;
    });

    // Get the most recent and second most recent assignments
    const currentAssignment = sortedAssignments[0];
    const previousAssignment = sortedAssignments[1];

    // Check if current user is not assigned in the current assignment
    const currentUserNotAssignedNow = !(
      currentAssignment.adminId === user.id ||
      currentAssignment.employeeId === user.id ||
      currentAssignment.assignedToEmployee === user.id
    );

    // Check if current user was assigned in the previous assignment
    const currentUserWasAssignedBefore =
      previousAssignment.adminId === user.id ||
      previousAssignment.employeeId === user.id ||
      previousAssignment.assignedToEmployee === user.id;

    // Project was transferred if user was previously assigned but is not assigned now
    return currentUserNotAssignedNow && currentUserWasAssignedBefore;
  };

  // Helper function to check if user has permission to edit a project based on its status
  const canEditProjectWithStatus = (project: any): boolean => {
    console.log('canEditProjectWithStatus - Checking project:', project.id || 'unknown id');

    // Check if project was transferred
    if (isProjectTransferred(project)) {
      console.log('canEditProjectWithStatus - Project was transferred');

      // If user has all three permissions (projectRead, projectCreate, projectEdit), they can still edit
      let hasAllThreePermissions = false;

      if (user) {
        let projectReadValue = false;
        let projectCreateValue = false;
        let projectEditValue = false;

        // Check if permissions is an object with numeric keys (like {0: {...}})
        if (typeof user.permissions === 'object' && '0' in user.permissions) {
          //@ts-ignore
          projectReadValue = Boolean(user.permissions[0].projectRead);
          //@ts-ignore
          projectCreateValue = Boolean(user.permissions[0].projectCreate);
          //@ts-ignore
          projectEditValue = Boolean(user.permissions[0].projectEdit);
        }
        // Check if permissions is an array
        else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
          projectReadValue = Boolean(user.permissions[0].projectRead);
          projectCreateValue = Boolean(user.permissions[0].projectCreate);
          projectEditValue = Boolean(user.permissions[0].projectEdit);
        }
        // Direct object access if neither of the above
        else if (typeof user.permissions === 'object') {
          projectReadValue = Boolean(user.permissions.projectRead);
          projectCreateValue = Boolean(user.permissions.projectCreate);
          projectEditValue = Boolean(user.permissions.projectEdit);
        }

        hasAllThreePermissions = projectReadValue && projectCreateValue && projectEditValue;
      }

      if (hasAllThreePermissions) {
        console.log('canEditProjectWithStatus - User has all three permissions (projectRead, projectCreate, projectEdit), allowing edit');
        return true;
      } else {
        console.log('canEditProjectWithStatus - User does not have all three permissions, cannot edit transferred project');
        return false;
      }
    }

    // Per new requirements: Always allow editing regardless of project status or user permissions
    console.log(
      'canEditProjectWithStatus - Status check removed, allowing edit regardless of status permissions'
    );

    // Return true to allow editing regardless of project status
    return true;
  };

  const handleRowClick = (row: any) => {
    if (row) {
      // Get most recent status for popup display
      let currentStatus = row.status;
      let rawProject = row.rawProject;

      // If raw project data is available, check for the most recent status
      if (rawProject?.projectAssigned && rawProject.projectAssigned.length > 0) {
        // Sort assignments by date (newest first)
        const sortedAssignments = [...rawProject.projectAssigned].sort(
          (a, b) =>
            new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime() -
            new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime()
        );

        // Use the status from the newest assignment for display
        const latestStatus = sortedAssignments[0].status;
        if (latestStatus) {
          currentStatus = formatStatusText(latestStatus);
          console.log('Using latest status for popup:', currentStatus);
        }
      }

      // Prepare assignment history data
      const history = formatAssignmentHistory(rawProject, employees, currentUser);
      setAssignmentHistory(history);

      // Reset assignment history visibility
      setShowAssignmentHistory(false);

      // Set the selected project data for the popup with updated status
      setSelectedProject({
        ...row,
        status: currentStatus,
        // Keep a reference to the original rawProject
        rawProject: rawProject,
      });

      // Open the popup
      projectDetailsPopup.openPopup();
    }
  };

  // Function to handle viewing origin project
  const handleViewOriginProject = async (originProject: any) => {
    console.log('handleViewOriginProject - Origin project data:', originProject);

    if (!originProject?.id) {
      toast.error('Origin project ID not found');
      return;
    }

    try {
      // Fetch origin project using Redux action
      await dispatch(fetchOriginProject(originProject.id)).unwrap();

      // After successful fetch, trigger the slide animation
      setViewingOriginProject(true);
    } catch (error: any) {
      console.error('Error fetching origin project:', error);
      toast.error(error || 'Failed to fetch origin project');
    }
  };

  // Function to close origin project view
  const handleCloseOriginProject = () => {
    setViewingOriginProject(false);
    dispatch(clearOriginProject());
  };

  // Function to view reopened project (project created from current)
  const handleViewReopenedProject = async (reopenedProj: any) => {
    console.log('handleViewReopenedProject - Reopened project data:', reopenedProj);

    if (!reopenedProj?.id) {
      toast.error('Reopened project ID not found');
      return;
    }

    try {
      // Fetch reopened project using Redux action
      await dispatch(fetchReopenedProject(reopenedProj.id)).unwrap();

      // After successful fetch, trigger the slide animation
      setViewingReopenedProject(true);
    } catch (error: any) {
      console.error('Error fetching reopened project:', error);
      toast.error(error || 'Failed to fetch reopened project');
    }
  };

  // Function to close reopened project view
  const handleCloseReopenedProject = () => {
    setViewingReopenedProject(false);
    dispatch(clearReopenedProject());
  };

  // Function to handle edit project button click
  const handleEditProject = (row: any) => {
    console.log('Project row data:', row);
    console.log('Raw project data being passed to edit page:', row.rawProject);

    // Use the actual unique ID of the project
    const projectId = row.rawProject?.id || row.id;

    // Get the raw status - ensure we're using the most recent status
    let rawStatus = row.rawProject?.status || row.status;

    // Check projectAssigned array for most recent status
    if (row.rawProject?.projectAssigned && row.rawProject.projectAssigned.length > 0) {
      // Sort assignments by date (newest first)
      const sortedAssignments = [...row.rawProject.projectAssigned].sort(
        (a, b) =>
          new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime() -
          new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime()
      );

      // Use the status from the newest assignment
      rawStatus = sortedAssignments[0].status || rawStatus;
      console.log('Using latest status for edit:', rawStatus);
    }

    // Get human readable formatted status
    const formattedStatus = formatStatusText(rawStatus);

    // Check if the current user is assigned to this project
    let isUserAssignedToProject = false;

    if (user?.id && row.rawProject) {
      // Check project assignment array
      if (row.rawProject.projectAssigned && row.rawProject.projectAssigned.length > 0) {
        isUserAssignedToProject = row.rawProject.projectAssigned.some(
          (assignment: any) =>
            assignment.adminId === user.id ||
            assignment.employeeId === user.id ||
            assignment.assignedToEmployee === user.id
        );
      }

      // Also check direct assignment fields as fallback
      if (!isUserAssignedToProject && row.rawProject.assignedEmployeeId === user.id) {
        isUserAssignedToProject = true;
      }

      if (!isUserAssignedToProject && row.rawProject.assignedToEmployee === user.id) {
        isUserAssignedToProject = true;
      }
    }

    console.log('Current user assigned to project:', isUserAssignedToProject);

    // Format the project data to ensure all needed fields are available
    const projectData = {
      ...row.rawProject,
      projectName: row.rawProject?.projectName || row.projectName,
      projectNumber: row.rawProject?.projectNumber || row.articleNo,
      status: rawStatus, // Send the original status code for backend compatibility
      displayStatus: formattedStatus, // Add the formatted display version
      ClientId: row.rawProject?.ClientId,
      projectDescription: row.rawProject?.projectDescription || '',
      comments: row.rawProject?.comments || '',
      projectAmount: row.rawProject?.projectAmount || 0,
      projectAssigned: row.rawProject?.projectAssigned || [],
      projectImages: row.rawProject?.projectImages || [],
      // Add explicit assignment information for the edit page
      userIsAssignedToThisProject: isUserAssignedToProject,
    };

    // Navigate to edit page with project ID in URL and all project data via state
    navigate(`/edit-project/${projectId}`, {
      state: {
        projectData: projectData,
      },
    });
  };

  // Handle search functionality
  const handleSearch = async (searchQuery: string) => {
    if (!searchQuery || searchQuery.trim() === '') {
      // If search is empty, reload projects based on current status filter
      setCurrentPage(1); // Reset to first page
      fetchProjects(1, itemsPerPage);
      return;
    }

    setLoading(true);
    setCurrentPage(1); // Reset to first page for new search

    // IMPORTANT: Clear cache before search to avoid stale data
    dispatch(clearCache());
    dispatch(clearStatusData());
    dispatch(clearProjectsCache());

    // Reset the processed flag so new search data will be processed
    hasProcessedDataRef.current = false;
    console.log('Reset hasProcessedDataRef for search to allow new data processing');

    console.log(`Searching projects with query: ${searchQuery} and status filter: ${statusFilter}`);

    try {
      let result;

      // Check if there's an active status filter
      if (statusFilter && statusFilter !== 'All') {
        // Search within the selected status
        const apiStatus = convertUIStatusToApiStatus(statusFilter);
        console.log(`Searching within status: ${apiStatus}`);
        result = await dispatch(
          fetchProjectsByStatus({
            status: apiStatus,
            search: searchQuery,
            page: 1,
            limit: itemsPerPage,
            customerName:
              filterValues.customerName && filterValues.customerName.trim() !== ''
                ? filterValues.customerName.trim()
                : undefined,
            articleNo:
              filterValues.articleNo && filterValues.articleNo.trim() !== ''
                ? filterValues.articleNo.trim()
                : undefined,
            createdFrom:
              filterValues.createdFrom && filterValues.createdFrom.trim() !== ''
                ? filterValues.createdFrom.trim()
                : undefined,
            createdTo:
              filterValues.createdTo && filterValues.createdTo.trim() !== ''
                ? filterValues.createdTo.trim()
                : undefined,
          })
        ).unwrap();
      } else {
        // Search across all projects
        console.log('Searching across all projects');
        result = await dispatch(
          searchProjects({
            search: searchQuery,
            page: 1,
            limit: itemsPerPage,
            customerName:
              filterValues.customerName && filterValues.customerName.trim() !== ''
                ? filterValues.customerName.trim()
                : undefined,
            articleNo:
              filterValues.articleNo && filterValues.articleNo.trim() !== ''
                ? filterValues.articleNo.trim()
                : undefined,
            createdFrom:
              filterValues.createdFrom && filterValues.createdFrom.trim() !== ''
                ? filterValues.createdFrom.trim()
                : undefined,
            createdTo:
              filterValues.createdTo && filterValues.createdTo.trim() !== ''
                ? filterValues.createdTo.trim()
                : undefined,
          })
        ).unwrap();
      }

      if (result && Array.isArray(result.data)) {
        const formattedProjects = formatProjectsForDisplay(result.data);
        setDisplayData(formattedProjects);

        // Update pagination if available
        if (result.pagination) {
          setCurrentPage(1); // Reset to first page for search results
          setTotalItems(result.pagination.total);
          setTotalPages(result.pagination.pages);
        }
      } else {
        // If no data property or invalid format, set empty display
        setDisplayData([]);
        setTotalItems(0);
        setTotalPages(1);
      }
    } catch (error: any) {
      console.error('Search error:', error);
      toast.error('Failed to search projects');
    } finally {
      setLoading(false);
    }
  };

  // Process data from Redux to display format
  const fetchProjects = async (page: number, perPage: number) => {
    // Prevent duplicate fetches
    if (loading || filterLoading || projectsLoading) {
      console.log(`Skipping fetchProjects call because we're already loading data`);
      return;
    }

    // Reset loading state
    setLoading(true);
    console.log(
      `Fetching projects for page ${page} with ${perPage} items per page, status: ${statusFilter}`
    );

    try {
      // Dispatch an action to fetch projects with pagination through Redux
      if (statusFilter && statusFilter !== 'All' && statusFilter !== '') {
        // For specific status filters
        const apiStatus = convertUIStatusToApiStatus(statusFilter);

        console.log(`Making API call for status ${apiStatus}, page ${page}`);

        // Create a structured object to pass pagination params and status
        const params = {
          status: apiStatus,
          page: page,
          limit: perPage,
          search: searchValue && searchValue.trim() !== '' ? searchValue.trim() : undefined,
          customerName:
            filterValues.customerName && filterValues.customerName.trim() !== ''
              ? filterValues.customerName.trim()
              : undefined,
          articleNo:
            filterValues.articleNo && filterValues.articleNo.trim() !== ''
              ? filterValues.articleNo.trim()
              : undefined,
          createdFrom:
            filterValues.createdFrom && filterValues.createdFrom.trim() !== ''
              ? filterValues.createdFrom.trim()
              : undefined,
          createdTo:
            filterValues.createdTo && filterValues.createdTo.trim() !== ''
              ? filterValues.createdTo.trim()
              : undefined,
        };

        // Make the API call and await the result
        const result = await dispatch(
          fetchProjectsByStatus({
            status: apiStatus,
            page: page,
            limit: perPage,
            search: searchValue && searchValue.trim() !== '' ? searchValue.trim() : undefined,
          })
        ).unwrap();

        console.log(`Received API response for ${apiStatus}, page ${page}:`, result);
      } else {
        // For "All" status - use loadProjects with pagination parameters
        console.log(`Making API call for All projects, page ${page}`);
        const result = await dispatch(
          loadProjects({
            page,
            limit: perPage,
            search: searchValue && searchValue.trim() !== '' ? searchValue.trim() : undefined,
          })
        ).unwrap();
        console.log(`Received API response for All projects, page ${page}:`, result);
      }

      // Add a small delay to ensure Redux state is updated
      await new Promise(resolve => setTimeout(resolve, 50));

      // Now format the projects from Redux for display
      if (reduxProjects && Array.isArray(reduxProjects)) {
        const formattedProjects = reduxProjects.map((project: any, index: number) => {
          // Get the latest status
          const latestStatus = getLatestProjectStatus(project);

          // Get customer name using our utility function
          let customerName = project.customerName;

          // Look up customer from Redux store if available
          if (project.ClientId && customers.length > 0) {
            const matchingCustomer = customers.find(c => c.id === project.ClientId);
            if (matchingCustomer) {
              customerName = matchingCustomer.companyName;
            }
          }

          // Ensure we don't show "Client #ID" fallback
          if (!customerName || customerName.startsWith('Client #')) {
            customerName = extractCustomerName(project, customers);
          }

          return {
            // Always use manakProjectId if available to ensure consistency
            id: project.manakProjectId ? `${project.manakProjectId}` : `Project-${index + 1}`,
            status: formatStatusText(latestStatus),
            customerName: customerName,
            articleNo: project.projectNumber || `NT-${3015 + index}`,
            projectName: project.projectName || `Project ${index + 1}`,
            createdOn: {
              date: project.createdAt
                ? formatDateToDDMMYY(project.createdAt)
                : formatDateToDDMMYY(new Date()),
              time: extractTimeFromDate(project.createdAt),
            },
            // Include the raw project data for reference
            rawProject: project,
          };
        });

        // Set the display data from formatted Redux projects (will be empty array if no projects)
        setDisplayData(formattedProjects);

        // Use the pagination data from Redux
        if (reduxPagination) {
          setCurrentPage(reduxPagination.page || 1);
          setTotalItems(reduxPagination.total || formattedProjects.length);

          console.log(
            `Setting pagination from Redux: page ${reduxPagination.page}, total ${reduxPagination.total}, totalPages ${reduxPagination.pages}`
          );

          // Use totalPages directly from the backend API pagination data
          const apiTotalPages = reduxPagination.pages || 1;
          console.log(`Using API provided totalPages value: ${apiTotalPages}`);
          console.log(
            `Raw pagination data from redux: total=${reduxPagination.total}, limit=${reduxPagination.limit}, page=${reduxPagination.page}`
          );
          setTotalPages(apiTotalPages);

          // Make sure totalItems is set correctly for the ResponsiveTable component
          // This is what the component will use to calculate pagination now
          setTotalItems(reduxPagination.total || formattedProjects.length);
        } else {
          // Fallback if no Redux pagination
          setTotalItems(formattedProjects.length);
          // Calculate total pages locally
          setTotalPages(Math.ceil(formattedProjects.length / itemsPerPage));
          console.log('No Redux pagination data available, calculating pagination locally');
        }

        // Data loading complete
        setInitialLoading(false);
      } else {
        // Handle no data scenario or invalid format
        console.log('No projects data available in Redux or invalid format');
        setDisplayData([]);
        setTotalItems(0);
        setTotalPages(1);
        setInitialLoading(false);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);

      // Fallback logic if Redux actions fail
      if (reduxProjects && Array.isArray(reduxProjects) && reduxProjects.length > 0) {
        console.log('Using existing Redux data as fallback');

        // Filter projects based on search and status
        let projectsToDisplay = [...reduxProjects];

        // Apply status filter if active
        if (statusFilter && statusFilter !== 'All' && statusFilter !== '') {
          const apiStatus = statusFilter.replace(/-/g, '_').toUpperCase();
          projectsToDisplay = projectsToDisplay.filter(project => {
            const projectStatus =
              project.status ||
              (project.projectAssigned && project.projectAssigned[0]?.status) ||
              '';
            return projectStatus === apiStatus;
          });
        }

        // Apply search filter if provided
        if (searchValue && searchValue.trim() !== '') {
          const searchLower = searchValue.toLowerCase().trim();
          projectsToDisplay = projectsToDisplay.filter(
            project =>
              (project.projectName && project.projectName.toLowerCase().includes(searchLower)) ||
              (project.projectNumber &&
                project.projectNumber.toLowerCase().includes(searchLower)) ||
              (project.manakProjectId &&
                String(project.manakProjectId).toLowerCase().includes(searchLower))
          );
        }

        // Format projects for display
        const formattedProjects = projectsToDisplay.map((project, index) => {
          const latestStatus = getLatestProjectStatus(project);
          const customerName = extractCustomerName(project, customers);

          return {
            id: project.manakProjectId ? `${project.manakProjectId}` : `Project-${index + 1}`,
            status: formatStatusText(latestStatus),
            customerName: customerName,
            articleNo: project.projectNumber || `NT-${3015 + index}`,
            projectName: project.projectName || `Project ${index + 1}`,
            createdOn: {
              date: project.createdAt
                ? formatDateToDDMMYY(project.createdAt)
                : formatDateToDDMMYY(new Date()),
              time: extractTimeFromDate(project.createdAt),
            },
            rawProject: project,
          };
        });

        // Apply pagination manually for Redux fallback data
        const startIndex = (page - 1) * perPage;
        const endIndex = startIndex + perPage;
        const paginatedData = formattedProjects.slice(startIndex, endIndex);

        setDisplayData(paginatedData);
        setTotalItems(formattedProjects.length);
      } else {
        // No fallback data available
        setDisplayData([]);
        setTotalItems(0);
      }

      setInitialLoading(false);
    } finally {
      console.log('Finished loading projects - resetting loading states');
      setLoading(false);
      setFilterLoading(false);
      setInitialLoading(false);
    }
  };

  // Helper function to format projects for display
  const formatProjectsForDisplay = (projects: any[]): any[] => {
    if (!Array.isArray(projects) || projects.length === 0) return [];

    return projects.map((project: any, index: number) => {
      // Get the latest status
      const latestStatus = getLatestProjectStatus(project);

      // Debug log for status filtering issues
      if (index < 3) {
        // Only log first 3 projects to avoid spam
        console.log(
          `Project ${project.id} - Latest Status: ${latestStatus}, Raw Assignments:`,
          project.projectAssigned
        );
      }

      // Get customer name using our utility function
      let customerName = project.customerName;

      // Look up customer from Redux store if available
      if (project.ClientId && customers.length > 0) {
        const matchingCustomer = customers.find(c => c.id === project.ClientId);
        if (matchingCustomer) {
          customerName = matchingCustomer.companyName;
        }
      }

      // Ensure we don't show "Client #ID" fallback
      if (!customerName || customerName.startsWith('Client #')) {
        customerName = extractCustomerName(project, customers);
      }

      return {
        // Always use manakProjectId if available to ensure consistency
        id: project.manakProjectId ? `${project.manakProjectId}` : `Project-${index + 1}`,
        status: formatStatusText(latestStatus),
        customerName: customerName,
        articleNo: project.projectNumber || `NT-${3015 + index}`,
        projectName: project.projectName || `Project ${index + 1}`,
        createdOn: {
          date: project.createdAt
            ? formatDateToDDMMYY(project.createdAt)
            : formatDateToDDMMYY(new Date()),
          time: extractTimeFromDate(project.createdAt),
        },
        // Include the raw project data for reference
        rawProject: project,
      };
    });
  };

  // Initialize data on component mount and when Redux data changes
  useEffect(() => {
    const projectsToCheck = reduxProjects;

    console.log('Redux data effect triggered:', {
      hasData: projectsToCheck && projectsToCheck.length > 0,
      hasProcessed: hasProcessedDataRef.current,
      filterLoading,
      projectsLength: projectsToCheck?.length,
      isInitialMount: isInitialMountRef.current,
      currentDisplayDataLength: displayData.length,
      searchValue,
      statusFilter,
    });

    // Process Redux data (including empty arrays) if we haven't processed it yet
    if (Array.isArray(projectsToCheck) && !hasProcessedDataRef.current) {
      console.log('Processing Redux projects data and setting display data');

      try {
        const formattedProjects = formatProjectsForDisplay(projectsToCheck);

        // Update the display data (this will be empty array if no projects)
        setDisplayData(formattedProjects);

        // Update pagination info if we have it
        if (reduxPagination) {
          setTotalItems(reduxPagination.total || formattedProjects.length);
          setTotalPages(
            reduxPagination.pages || Math.ceil(formattedProjects.length / itemsPerPage)
          );
        } else {
          setTotalItems(formattedProjects.length);
          setTotalPages(Math.ceil(formattedProjects.length / itemsPerPage));
        }

        // Mark that we've processed the data
        hasProcessedDataRef.current = true;
        console.log(
          'Data processing complete, hasProcessedDataRef set to true, displayData length:',
          formattedProjects.length
        );

        // Mark that initial mount is complete after processing data
        setTimeout(() => {
          isInitialMountRef.current = false;
        }, 100);
      } catch (error) {
        console.error('Error processing Redux projects data:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxProjects, reduxPagination, searchValue]);

  // Fallback effect to ensure data gets displayed if the main effect doesn't work
  useEffect(() => {
    // If we have Redux data (including empty arrays) but no display data and we're not loading, force display the data
    // BUT: Don't do this if we just cleared the search and are waiting for fresh data
    if (
      Array.isArray(reduxProjects) &&
      displayData.length === 0 &&
      !reduxLoading &&
      !filterLoading &&
      !hasProcessedDataRef.current &&
      !(searchValue === '' && reduxProjects.length > 0) // Prevent fallback when search was just cleared
    ) {
      console.log('Fallback: Force processing Redux data to display');
      try {
        const formattedProjects = formatProjectsForDisplay(reduxProjects);
        setDisplayData(formattedProjects);
        hasProcessedDataRef.current = true;
      } catch (error) {
        console.error('Error in fallback data processing:', error);
      }
    }
  }, [reduxProjects, displayData.length, reduxLoading, filterLoading, searchValue]);

  // REMOVING THIS EFFECT - it was causing double API calls
  // useEffect(() => {
  //   const projectsToCheck = reduxProjects;
  //   if (projectsToCheck && projectsToCheck.length > 0 && !loading) {
  //     console.log(`Page or items per page changed - fetching page ${currentPage}`);
  //     fetchProjects(currentPage, itemsPerPage);
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [currentPage, itemsPerPage]);

  // DISABLED - This effect was conflicting with the main search effect above
  // Handle search
  // useEffect(() => {
  //   // Skip if we're already loading
  //   if (loading || filterLoading) {
  //     return;
  //   }

  //   // Reset to first page when searching
  //   setCurrentPage(1);

  //   // Add debounce for search
  //   const timer = setTimeout(() => {
  //     // Only make API call if we're not already loading
  //     if (!loading && !filterLoading) {
  //       // Clear any cache to ensure fresh results
  //       dispatch(clearCache());
  //       fetchProjects(1, itemsPerPage);
  //     }
  //   }, 300);

  //   return () => clearTimeout(timer);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [searchValue]);

  // Show loading state while Redux is loading
  useEffect(() => {
    setLoading(reduxLoading);

    // If redux is not loading anymore and we have data, set initialLoading to false
    if (!reduxLoading && reduxProjects && reduxProjects.length > 0) {
      setInitialLoading(false);
    }
  }, [reduxLoading, reduxProjects]);

  // Add effect to sync with Redux pagination data
  useEffect(() => {
    if (reduxPagination) {
      console.log('Redux pagination updated:', reduxPagination);
      // Only update if we're not currently loading to avoid race conditions
      if (!loading && !filterLoading) {
        if (reduxPagination.page !== currentPage) {
          console.log(
            `Pagination sync: Updating current page from ${currentPage} to ${reduxPagination.page}`
          );
          setCurrentPage(reduxPagination.page);
        }
        if (reduxPagination.total !== totalItems) {
          console.log(
            `Pagination sync: Updating total items from ${totalItems} to ${reduxPagination.total}`
          );
          setTotalItems(reduxPagination.total);
        }
        if (reduxPagination.pages !== totalPages) {
          console.log(
            `Pagination sync: Updating total pages from ${totalPages} to ${reduxPagination.pages}`
          );
          setTotalPages(reduxPagination.pages);
        }
      }
    }
  }, [reduxPagination, loading, filterLoading]);

  // Show error if Redux has an error
  useEffect(() => {
    if (reduxError) {
      console.error('Redux error:', reduxError);
    }
  }, [reduxError]);

  // Function to handle checking for out-of-warranty projects
  const handleCheckWarranty = async () => {
    try {
      setLoading(true);
      // Make API call to check warranty status
      const response = await axiosInstance.post('/v1/admin/project/check-warranty');

      if (response.data?.success) {
        alert(
          `Warranty check completed: ${response.data.updatedCount} projects marked as Non Active`
        );
        // Refresh projects to show updated statuses
        dispatch(
          loadProjects({
            page: 1,
            limit: itemsPerPage,
            search: searchValue && searchValue.trim() !== '' ? searchValue.trim() : undefined,
          })
        );
      } else {
        alert('Warranty check failed. Please try again.');
      }
    } catch (error) {
      console.error('Error checking project warranties:', error);
      alert('Error checking project warranties. Please check the console for details.');
    } finally {
      setLoading(false);
    }
  };

  // Update the handler for create project button
  const handleCreateProject = () => {
    navigate('/add-project'); // Navigate to add project page
  };

  // Add image viewer state
  const [viewerImage, setViewerImage] = useState<string>('');
  const [isImageViewerOpen, setIsImageViewerOpen] = useState<boolean>(false);

  // Function to open the image viewer
  const openImageViewer = (imageUrl: string) => {
    setViewerImage(imageUrl);
    setIsImageViewerOpen(true);
  };

  // Function to close the image viewer
  const closeImageViewer = () => {
    setIsImageViewerOpen(false);
  };

  // Check for status filter passed in from Dashboard
  useEffect(() => {
    // Check if we have a special filter type (IN_PIPELINE) from Dashboard
    if (location.state?.filterType === 'IN_PIPELINE' && location.state?.fromDashboard) {
      console.log('IN_PIPELINE filter received from Dashboard');
      const { dateRange } = location.state;
      
      // Set flag to prevent duplicate calls
      isDashboardFilterProcessingRef.current = true;
      
      // Set a special display filter
      setStatusFilter('In Pipeline');
      setFilterLoading(true);
      
      // Fetch projects with IN_PIPELINE statuses (UNASSIGNED, ASSIGNED, IN_PROGRESS, REVIEW, DELIVERED)
      const fetchInPipelineProjects = async () => {
        try {
          const inPipelineStatuses = ['UNASSIGNED', 'ASSIGNED', 'IN_PROGRESS', 'REVIEW', 'DELIVERED'];
          const allResults = [];
          
          // Fetch projects for each status
          for (const status of inPipelineStatuses) {
            const result = await dispatch(
              fetchProjectsByStatus({
                status,
                page: 1,
                limit: 100, // Higher limit to get more results
                createdFrom: dateRange?.from,
                createdTo: dateRange?.to,
              })
            ).unwrap();
            
            if (result && Array.isArray(result.data)) {
              allResults.push(...result.data);
            }
          }
          
          // Remove duplicates (if any) and format
          const uniqueProjects = Array.from(
            new Map(allResults.map(project => [project.id, project])).values()
          );
          
          const formattedProjects = formatProjectsForDisplay(uniqueProjects);
          setDisplayData(formattedProjects);
          setTotalItems(uniqueProjects.length);
          setTotalPages(Math.ceil(uniqueProjects.length / itemsPerPage));
          
          console.log(`Found ${uniqueProjects.length} IN_PIPELINE projects`);
        } catch (error) {
          console.error('Error fetching IN_PIPELINE projects:', error);
          toast.error('Failed to load projects');
        } finally {
          setFilterLoading(false);
          
          // Clear location state
          navigate(location.pathname, { replace: true, state: {} });
          
          setTimeout(() => {
            isDashboardFilterProcessingRef.current = false;
          }, 200);
        }
      };
      
      fetchInPipelineProjects();
      return;
    }
    
    // Check if we have a status filter in location state (coming from Dashboard)
    if (location.state?.statusFilter && location.state?.fromDashboard) {
      const dashboardStatusFilter = location.state.statusFilter;
      console.log('Status filter received from Dashboard:', dashboardStatusFilter);

      // Set flag to prevent duplicate calls from auto-trigger effect
      isDashboardFilterProcessingRef.current = true;

      // First update the statusFilter state so the ProjectFilterSlider shows the correct filter
      setCurrentStatusFilter(dashboardStatusFilter);

      // Apply the filter with a small delay to ensure components are updated properly
      setFilterLoading(true);
      setTimeout(() => {
        handleStatusFilterChange(dashboardStatusFilter);

        // Clear the location state to prevent reapplying the filter on subsequent renders
        navigate(location.pathname, { replace: true, state: {} });

        // Clear the processing flag after a short delay
        setTimeout(() => {
          isDashboardFilterProcessingRef.current = false;
        }, 200);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // In the main return block, before rendering the main content, add:
  if (!hasProjectPermission) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <IoClose className="text-6xl text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-700 mb-2">Access Denied</h2>
        <p className="text-gray-500 text-center max-w-md">
          You do not have permission to view projects. Please contact your administrator for access.
        </p>
      </div>
    );
  }

  return (
    <React.Fragment>
      <ComponentWrapper
        icon={<MdOutlineFeaturedPlayList size={30} />}
        title={statusFilter === 'In Pipeline' ? 'In Pipeline Projects' : 'Projects'}
        buttonOne={
          <>
            {/* Back to Dashboard button - shown only for In Pipeline view */}
            {statusFilter === 'In Pipeline' && (
              <Button
                variant="outline"
                size="sm"
                className="py-2 mr-3 border-gray-400 text-gray-600 hover:bg-gray-100"
                onClick={() => navigate('/dashboard')}
              >
                ← Back to Dashboard
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              className="py-2 mr-3"
              icon={<MdRefresh size={16} />}
              onClick={() => {
                // Reset the processed data flag to allow fresh data processing
                hasProcessedDataRef.current = false;
                // Reset initial mount flag to ensure fresh data loading
                isInitialMountRef.current = false;

                // Clear current display data
                setDisplayData([]);

                // Only refresh if we have a valid status filter or user has clicked All
                if (statusFilter === 'All') {
                  // Set that user has clicked All so data will be displayed
                  hasUserClickedAllRef.current = true;

                  dispatch(clearCache());
                  dispatch(
                    loadProjects({
                      page: currentPage,
                      limit: itemsPerPage,
                      search:
                        searchValue && searchValue.trim() !== '' ? searchValue.trim() : undefined,
                    })
                  )
                    .unwrap()
                    .then(() => {
                      toast.success('Projects data refreshed');
                    })
                    .catch(err => {
                      toast.error('Failed to refresh projects data');
                      console.error(err);
                    });
                } else if (statusFilter && statusFilter !== '') {
                  // For other status filters, use the appropriate action
                  const apiFilter = convertUIStatusToApiStatus(statusFilter);
                  dispatch(clearCache());
                  dispatch(
                    fetchProjectsByStatus({
                      status: apiFilter,
                      page: currentPage,
                      limit: itemsPerPage,
                      search:
                        searchValue && searchValue.trim() !== '' ? searchValue.trim() : undefined,
                    })
                  )
                    .unwrap()
                    .then(() => {
                      toast.success('Projects data refreshed');
                    })
                    .catch(err => {
                      toast.error('Failed to refresh projects data');
                      console.error(err);
                    });
                } else {
                  // No status selected, just show success message
                  toast.success('Please select a status to view projects');
                }
              }}
            >
              Refresh
            </Button>
            {/* Only show Create Project button if user has projectWrite permission */}
            {(() => {
              // Check if user is superAdmin or has projectWrite permission
              let hasProjectWritePermission = false;

              if (user?.designation === 'superAdmin') {
                hasProjectWritePermission = true;
              } else if (user?.permissions) {
                // Check if permissions is an object with numeric keys (like {0: {...}})
                if (typeof user.permissions === 'object' && '0' in user.permissions) {
                  //@ts-ignore
                  hasProjectWritePermission = Boolean(user.permissions[0].projectWrite);
                }
                // Check if permissions is an array
                else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
                  //@ts-ignore
                  hasProjectWritePermission = Boolean(user.permissions[0].projectWrite);
                }
                // Direct object access if neither of the above
                else if (typeof user.permissions === 'object') {
                  //@ts-ignore
                  hasProjectWritePermission = Boolean(user.permissions.projectWrite);
                }
              }

              return hasProjectWritePermission ? (
                <Button
                  variant="primary"
                  size="sm"
                  className="py-2 mr-3 text-white"
                  icon={<FiPlus size={16} />}
                  onClick={handleCreateProject}
                >
                  Create Project
                </Button>
              ) : null;
            })()}
          </>
        }
        buttonsTwo={[
          {
            component: hasProjectPermission ? (
              <Button
                variant="secondary"
                size="sm"
                className="py-2 ml-1" // Keeping the margin-left for spacing
                icon={<RxDownload size={16} />}
                onClick={async () => {
                  toast.success('Downloading project data as Excel file...');
                  try {
                    // Import axios instance
                    const axiosInstance = (await import('../axios')).default;

                    // Make authenticated request to get the Excel file
                    const response = await axiosInstance.get('/v1/admin/project/export-excel', {
                      responseType: 'blob', // Important for file downloads
                    });

                    // Create a blob from the response data
                    const blob = new Blob([response.data], {
                      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    });

                    // Create a temporary URL for the blob
                    const url = window.URL.createObjectURL(blob);

                    // Create a temporary link element
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'projects.xlsx';

                    // Append to the document, click it, and remove it
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    // Release the blob URL
                    window.URL.revokeObjectURL(url);
                  } catch (error) {
                    console.error('Error downloading data:', error);
                    toast.error('There was a problem downloading the data. Please try again.');
                  }
                }}
              >
                Download Data
              </Button>
            ) : null,
          },
        ]}
      >
        {/* Create a flex container to place toggle, slider, and search/filter */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4">
          {/* Left side: Project Filter Slider */}
          <div className="w-full md:flex-1 md:max-w-[65%] flex items-center gap-2">
            {/* Project Filter Slider with full width - disabled when filter panel is shown */}
            <div
              className={`w-full transition-all duration-300 ${showFilterPanel ? 'opacity-50 pointer-events-none filter blur-[0.5px]' : ''}`}
            >
              <ProjectFilterSlider
                key={`project-filter-${statusFilter}`}
                onFilterChange={handleStatusFilterChange}
                defaultFilter={statusFilter} // Now statusFilter is 'All' initially
              />
            </div>
          </div>

          {/* Right side: Search and Filter (takes remaining space) */}
          <div className="w-full md:flex-1 md:max-w-[33%] flex items-center">
            <div className="w-full">
              <TableSearchFilter
                searchPlaceholder="Search projects..."
                searchValue={searchValue}
                onSearchChange={setSearchValue}
                onClearSearch={() => {
                  console.log(
                    `[Projects] Search cleared via clear button - resetting to status filter: "${statusFilter}"`
                  );
                  setSearchValue('');

                  // Immediately clear current data and show loading
                  setDisplayData([]);
                  setFilterLoading(true);
                  setCurrentPage(1);

                  // Clear caches and trigger fresh data load based on current status filter
                  dispatch(clearCache());
                  dispatch(clearStatusData());
                  dispatch(clearProjectsCache());

                  // Reset the processed data flag
                  hasProcessedDataRef.current = false;

                  // Immediately trigger data load based on current status filter
                  setTimeout(() => {
                    if (statusFilter === 'All' && hasUserClickedAllRef.current) {
                      console.log('Loading all projects after search clear');
                      dispatch(loadProjects({ page: 1, limit: itemsPerPage }))
                        .unwrap()
                        .then(result => {
                          if (result && Array.isArray(result.data)) {
                            const formattedProjects = formatProjectsForDisplay(result.data);
                            setDisplayData(formattedProjects);
                            if (result.pagination) {
                              setTotalItems(result.pagination.total);
                              setTotalPages(result.pagination.pages);
                            }
                          } else {
                            setDisplayData([]);
                            setTotalItems(0);
                            setTotalPages(1);
                          }
                          setFilterLoading(false);
                        })
                        .catch(error => {
                          console.error('Error reloading all projects after search clear:', error);
                          setFilterLoading(false);
                        });
                    } else if (statusFilter && statusFilter !== 'All') {
                      console.log(
                        `Loading projects with status ${statusFilter} after search clear`
                      );
                      const apiStatus = convertUIStatusToApiStatus(statusFilter);
                      dispatch(
                        fetchProjectsByStatus({ status: apiStatus, page: 1, limit: itemsPerPage })
                      )
                        .unwrap()
                        .then(result => {
                          if (result && Array.isArray(result.data)) {
                            const formattedProjects = formatProjectsForDisplay(result.data);
                            setDisplayData(formattedProjects);
                            if (result.pagination) {
                              setTotalItems(result.pagination.total);
                              setTotalPages(result.pagination.pages);
                            }
                          } else {
                            setDisplayData([]);
                            setTotalItems(0);
                            setTotalPages(1);
                          }
                          setFilterLoading(false);
                        })
                        .catch(error => {
                          console.error(
                            `Error reloading projects with status ${statusFilter} after search clear:`,
                            error
                          );
                          setFilterLoading(false);
                        });
                    }
                  }, 100);
                }}
                onFilterClick={() => {
                  // Toggle filter panel visibility
                  const newPanelState = !showFilterPanel;
                  setShowFilterPanel(newPanelState);

                  // If closing the panel
                  if (!newPanelState) {
                    console.log(
                      `[Projects] Closing filter panel - clearing search and resetting to status filter: "${statusFilter}"`
                    );

                    // Clear the search value when closing the filter panel
                    setSearchValue('');

                    // If a status filter was set in the panel, update the status filter slider to match
                    if (filterValues.status !== 'All') {
                      setStatusFilter(filterValues.status);
                    }

                    // Reset all filter values to their default state
                    setFilterValues({
                      customerName: '',
                      articleNo: '',
                      status: 'All',
                      createdFrom: '',
                      createdTo: '',
                    });

                    // Also reset the debounced filters
                    setDebouncedFilters({
                      customerName: '',
                      articleNo: '',
                      status: 'All',
                      createdFrom: '',
                      createdTo: '',
                    });

                    // Reset the current page to 1
                    setCurrentPage(1);

                    // Clear current data and show loading
                    setDisplayData([]);
                    setFilterLoading(true);

                    // Clear caches and trigger fresh data load based on current status filter
                    dispatch(clearCache());
                    dispatch(clearStatusData());
                    dispatch(clearProjectsCache());

                    // Reset the processed data flag
                    hasProcessedDataRef.current = false;

                    // Immediately trigger data load based on current status filter (without search)
                    setTimeout(() => {
                      if (statusFilter === 'All' && hasUserClickedAllRef.current) {
                        console.log('Loading all projects after closing filter panel');
                        dispatch(loadProjects({ page: 1, limit: itemsPerPage }))
                          .unwrap()
                          .then(result => {
                            if (result && Array.isArray(result.data)) {
                              const formattedProjects = formatProjectsForDisplay(result.data);
                              setDisplayData(formattedProjects);
                              if (result.pagination) {
                                setTotalItems(result.pagination.total);
                                setTotalPages(result.pagination.pages);
                              }
                            } else {
                              setDisplayData([]);
                              setTotalItems(0);
                              setTotalPages(1);
                            }
                            setFilterLoading(false);
                          })
                          .catch(error => {
                            console.error(
                              'Error reloading all projects after closing filter panel:',
                              error
                            );
                            setFilterLoading(false);
                          });
                      } else if (statusFilter && statusFilter !== 'All') {
                        console.log(
                          `Loading projects with status ${statusFilter} after closing filter panel`
                        );
                        const apiStatus = convertUIStatusToApiStatus(statusFilter);
                        dispatch(
                          fetchProjectsByStatus({ status: apiStatus, page: 1, limit: itemsPerPage })
                        )
                          .unwrap()
                          .then(result => {
                            if (result && Array.isArray(result.data)) {
                              const formattedProjects = formatProjectsForDisplay(result.data);
                              setDisplayData(formattedProjects);
                              if (result.pagination) {
                                setTotalItems(result.pagination.total);
                                setTotalPages(result.pagination.pages);
                              }
                            } else {
                              setDisplayData([]);
                              setTotalItems(0);
                              setTotalPages(1);
                            }
                            setFilterLoading(false);
                          })
                          .catch(error => {
                            console.error(
                              `Error reloading projects with status ${statusFilter} after closing filter panel:`,
                              error
                            );
                            setFilterLoading(false);
                          });
                      }
                    }, 100);
                  }
                }}
                showFilterPanel={showFilterPanel}
                fullWidthSearch={true}
              />
            </div>
          </div>
        </div>

        {/* Use our reusable FilterPanel component */}
        {showFilterPanel && (
          <FilterPanel
            filterValues={filterValues}
            onFilterChange={handleFilterChange}
            statusOptions={[
              { value: 'All', label: 'All' },
              { value: 'IN_PROGRESS', label: 'In Progress', color: 'rgb(219 234 254)' },
              { value: 'DELIVERED', label: 'Delivered', color: 'rgb(220 252 231)' },
              { value: 'REVIEW', label: 'Review', color: 'rgb(254 249 195)' },
              { value: 'ASSIGNED', label: 'Assigned', color: 'rgb(224 231 255)' },
              { value: 'BILLED', label: 'Billed', color: 'rgb(254 226 226)' },
              { value: 'HOLD', label: 'On Hold', color: 'rgb(255 237 213)' },
              { value: 'CANCELLED', label: 'Cancelled', color: 'rgb(254 226 226)' },
              { value: 'PAYMENT_RECEIVED', label: 'Payment Received', color: 'rgb(220 252 231)' },
              { value: 'REOPEN', label: 'Reopen', color: 'rgb(243 232 255)' },
              { value: 'UNASSIGNED', label: 'Unassigned', color: 'rgb(243 244 246)' },
              { value: 'NON_ACTIVE', label: 'Non Active', color: 'rgb(156 163 175)' },
            ]}
            className="mt-3 mb-6"
          />
        )}

        {/* Existing table display code... */}
        <div className="bg-transparent md:bg-white rounded-lg">
          {/* Show skeleton loading during initial load, filter change, or when we have no display data yet */}
          {(() => {
            // Don't show skeleton if no status filter is selected (empty string)
            if (!statusFilter) {
              return false;
            }

            // Show skeleton if:
            // 1. We're actively loading (any kind of loading)
            // 2. OR we have no display data AND we're not in an error state AND we haven't confirmed empty data
            const isActivelyLoading =
              initialLoading || filterLoading || projectsLoading || reduxLoading;
            const hasNoDisplayData = displayData.length === 0;
            const isErrorState = !!reduxError;
            const isConfirmedEmpty = reduxProjects && reduxProjects.length === 0 && !reduxLoading;

            const shouldShowSkeleton =
              isActivelyLoading || (hasNoDisplayData && !isErrorState && !isConfirmedEmpty);

            return shouldShowSkeleton;
          })() ? (
            <SkeletonTable rows={itemsPerPage} />
          ) : displayData.length > 0 ? (
            <ResponsiveTable
              data={displayData}
              columns={columns}
              onRowClick={handleRowClick}
              statusColors={statusColors}
              statusIcons={statusIcons}
              itemsPerPageOptions={[10, 15, 20, 50]}
              defaultItemsPerPage={itemsPerPage}
              totalItems={totalItems}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={page => {
                console.log(`User clicked on page ${page}`);

                // Prevent duplicate calls
                if (page === currentPage || loading || filterLoading) {
                  console.log(`Skipping duplicate page change to ${page}`);
                  return;
                }

                // Set loading state to true
                setLoading(true);

                // Update current page
                setCurrentPage(page);

                // Reset the processed flag so new page data will be processed
                hasProcessedDataRef.current = false;
                console.log(
                  'Reset hasProcessedDataRef for page change to allow new data processing'
                );

                // Clear Redux cache to force fresh API call
                dispatch(clearCache());

                console.log(`Forcing fresh data load for page ${page}`);

                // If the filter panel is active, apply filters when changing page
                if (showFilterPanel) {
                  const hasPanelStatusFilter =
                    debouncedFilters.status && debouncedFilters.status !== 'All';
                  const panelStatus = hasPanelStatusFilter
                    ? convertUIStatusToApiStatus(debouncedFilters.status)
                    : '';

                  const filteredRequestParams = {
                    page,
                    limit: itemsPerPage,
                    search:
                      searchValue && searchValue.trim() !== '' ? searchValue.trim() : undefined,
                    customerName:
                      debouncedFilters.customerName &&
                      debouncedFilters.customerName.trim() !== ''
                        ? debouncedFilters.customerName.trim()
                        : undefined,
                    articleNo:
                      debouncedFilters.articleNo && debouncedFilters.articleNo.trim() !== ''
                        ? debouncedFilters.articleNo.trim()
                        : undefined,
                    createdFrom:
                      debouncedFilters.createdFrom && debouncedFilters.createdFrom.trim() !== ''
                        ? debouncedFilters.createdFrom.trim()
                        : undefined,
                    createdTo:
                      debouncedFilters.createdTo && debouncedFilters.createdTo.trim() !== ''
                        ? debouncedFilters.createdTo.trim()
                        : undefined,
                  };

                  const request = panelStatus
                    ? dispatch(
                        fetchProjectsByStatus({
                          ...filteredRequestParams,
                          status: panelStatus,
                        })
                      )
                    : dispatch(loadProjects(filteredRequestParams));

                  request
                    .unwrap()
                    .then(result => {
                      console.log(
                        panelStatus
                          ? `Successfully loaded filtered page ${page} for status ${panelStatus}`
                          : `Successfully loaded filtered page ${page} with filter panel active`
                      );

                      if (result && Array.isArray(result.data)) {
                        const formattedProjects = formatProjectsForDisplay(result.data);
                        setDisplayData(formattedProjects);

                        if (result.pagination) {
                          setTotalItems(result.pagination.total);
                          setTotalPages(result.pagination.pages);
                        }
                      } else {
                        setDisplayData([]);
                        setTotalItems(0);
                        setTotalPages(1);
                      }

                      setLoading(false);
                    })
                    .catch(error => {
                      console.error(`Error fetching filtered projects for page ${page}:`, error);
                      setLoading(false);
                    });
                } else {
                  // Check if we have an active search query
                  if (searchValue && searchValue.trim() !== '') {
                    // Check if there's also a status filter active
                    if (statusFilter && statusFilter !== 'All') {
                      // Search within specific status
                      const apiStatus = convertUIStatusToApiStatus(statusFilter);
                      console.log(
                        `Fetching search results page ${page} for query: ${searchValue} within status: ${apiStatus}`
                      );
                      dispatch(
                        fetchProjectsByStatus({
                          status: apiStatus,
                          search: searchValue.trim(),
                          page: page,
                          limit: itemsPerPage,
                          customerName:
                            debouncedFilters.customerName &&
                              debouncedFilters.customerName.trim() !== ''
                              ? debouncedFilters.customerName.trim()
                              : undefined,
                          articleNo:
                            debouncedFilters.articleNo && debouncedFilters.articleNo.trim() !== ''
                              ? debouncedFilters.articleNo.trim()
                              : undefined,
                          createdFrom:
                            debouncedFilters.createdFrom &&
                              debouncedFilters.createdFrom.trim() !== ''
                              ? debouncedFilters.createdFrom.trim()
                              : undefined,
                          createdTo:
                            debouncedFilters.createdTo && debouncedFilters.createdTo.trim() !== ''
                              ? debouncedFilters.createdTo.trim()
                              : undefined,
                        })
                      )
                        .unwrap()
                        .then(result => {
                          console.log(
                            `Successfully loaded search page ${page} for query: ${searchValue} within status: ${apiStatus}`
                          );

                          // Directly update UI without triggering fetchProjects
                          if (result && Array.isArray(result.data)) {
                            const formattedProjects = formatProjectsForDisplay(result.data);
                            setDisplayData(formattedProjects);

                            // Update pagination data
                            if (result.pagination) {
                              setTotalItems(result.pagination.total);
                              setTotalPages(result.pagination.pages);
                            }
                          } else {
                            // If no data property or invalid format, set empty display
                            setDisplayData([]);
                            setTotalItems(0);
                            setTotalPages(1);
                          }
                          setLoading(false);
                        })
                        .catch(error => {
                          console.error(`Error fetching search results for page ${page}:`, error);
                          setLoading(false);
                        });
                    } else {
                      // Search across all projects
                      console.log(`Fetching search results page ${page} for query: ${searchValue}`);
                      dispatch(
                        searchProjects({
                          search: searchValue.trim(),
                          page: page,
                          limit: itemsPerPage,
                          customerName:
                            debouncedFilters.customerName &&
                              debouncedFilters.customerName.trim() !== ''
                              ? debouncedFilters.customerName.trim()
                              : undefined,
                          articleNo:
                            debouncedFilters.articleNo && debouncedFilters.articleNo.trim() !== ''
                              ? debouncedFilters.articleNo.trim()
                              : undefined,
                          createdFrom:
                            debouncedFilters.createdFrom &&
                              debouncedFilters.createdFrom.trim() !== ''
                              ? debouncedFilters.createdFrom.trim()
                              : undefined,
                          createdTo:
                            debouncedFilters.createdTo && debouncedFilters.createdTo.trim() !== ''
                              ? debouncedFilters.createdTo.trim()
                              : undefined,
                        })
                      )
                        .unwrap()
                        .then(result => {
                          console.log(
                            `Successfully loaded search page ${page} for query: ${searchValue}`
                          );

                          // Directly update UI without triggering fetchProjects
                          if (result && Array.isArray(result.data)) {
                            const formattedProjects = formatProjectsForDisplay(result.data);
                            setDisplayData(formattedProjects);

                            // Update pagination data
                            if (result.pagination) {
                              setTotalItems(result.pagination.total);
                              setTotalPages(result.pagination.pages);
                            }
                          } else {
                            // If no data property or invalid format, set empty display
                            setDisplayData([]);
                            setTotalItems(0);
                            setTotalPages(1);
                          }
                          setLoading(false);
                        })
                        .catch(error => {
                          console.error(`Error fetching search results for page ${page}:`, error);
                          setLoading(false);
                        });
                    }
                  } else if (statusFilter && statusFilter !== 'All') {
                    const apiStatus = convertUIStatusToApiStatus(statusFilter);
                    console.log(
                      `Fetching filtered page ${page} for status ${apiStatus} with search: ${searchValue}`
                    );
                    dispatch(
                      fetchProjectsByStatus({
                        status: apiStatus,
                        page: page,
                        limit: itemsPerPage,
                        search:
                          searchValue && searchValue.trim() !== '' ? searchValue.trim() : undefined,
                      })
                    )
                      .unwrap()
                      .then(result => {
                        console.log(`Successfully loaded page ${page} for status ${apiStatus}`);

                        // Directly update UI without triggering fetchProjects
                        if (result && Array.isArray(result.data)) {
                          const formattedProjects = formatProjectsForDisplay(result.data);
                          setDisplayData(formattedProjects);

                          // Update pagination data
                          if (result.pagination) {
                            setTotalItems(result.pagination.total);
                            setTotalPages(result.pagination.pages);
                          }
                        } else {
                          // If no data property or invalid format, set empty display
                          setDisplayData([]);
                          setTotalItems(0);
                          setTotalPages(1);
                        }
                        setLoading(false);
                      })
                      .catch(error => {
                        console.error(`Error fetching projects for page ${page}:`, error);
                        setLoading(false);
                      });
                  } else if (statusFilter === 'All' && hasUserClickedAllRef.current) {
                    console.log(`Fetching all projects page ${page} with search: ${searchValue}`);
                    dispatch(
                      loadProjects({
                        page,
                        limit: itemsPerPage,
                        search:
                          searchValue && searchValue.trim() !== '' ? searchValue.trim() : undefined,
                      })
                    )
                      .unwrap()
                      .then(result => {
                        console.log(`Successfully loaded page ${page} for all projects`);

                        // Directly update UI without triggering fetchProjects
                        if (result && Array.isArray(result.data)) {
                          const formattedProjects = formatProjectsForDisplay(result.data);
                          setDisplayData(formattedProjects);

                          // Update pagination data
                          if (result.pagination) {
                            setTotalItems(result.pagination.total);
                            setTotalPages(result.pagination.pages);
                          }
                        } else {
                          // If no data property or invalid format, set empty display
                          setDisplayData([]);
                          setTotalItems(0);
                          setTotalPages(1);
                        }
                        setLoading(false);
                      })
                      .catch(error => {
                        console.error(`Error fetching projects for page ${page}:`, error);
                        setLoading(false);
                      });
                  } else {
                    // User hasn't clicked "All" yet, don't load data
                    console.log(
                      "All is selected but user hasn't clicked it yet, skipping pagination"
                    );
                    setLoading(false);
                  }
                }
              }}
              onItemsPerPageChange={size => {
                setItemsPerPage(size);
                setCurrentPage(1);

                // If the filter panel is active, apply filters when changing items per page
                if (showFilterPanel) {
                  // The useEffect for debouncedFilters will trigger applyFilters
                  // after we update itemsPerPage and currentPage
                } else {
                  // Otherwise just fetch with the new size
                  fetchProjects(1, size);
                }
              }}
              isLoading={filterLoading} // Pass loading state to table component
            />
          ) : (
            <div className="text-center py-8 text-gray-500">
              {reduxError ? (
                `Error loading projects: ${reduxError}`
              ) : !statusFilter ? (
                // Show message when no status filter is selected (empty string)
                <div className="flex flex-col items-center space-y-4">
                  <div className="text-6xl mb-4">📁</div>
                  <p className="text-lg font-medium">Select a status to view projects</p>
                  <p className="text-sm max-w-lg text-gray-400">
                    Choose a project status from the filter tabs above to see the corresponding
                    projects.
                  </p>
                </div>
              ) : (
                // Show message when status is selected but no projects found
                <div className="flex flex-col items-center space-y-4">
                  <p className="text-lg">No projects found</p>
                  <p className="text-sm max-w-lg">
                    {statusFilter === 'All'
                      ? 'There are currently no projects in the database. Create a new project to get started.'
                      : `No projects found with status "${statusFilter}".`}
                  </p>
                  {statusFilter === 'All' && (
                    <Button
                      variant="primary"
                      size="sm"
                      className="py-2 mt-4"
                      icon={<FiPlus size={16} />}
                      onClick={handleCreateProject}
                    >
                      Create Project
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </ComponentWrapper>

      {/* Project Details Popup */}
      {selectedProject && (
        <PopupWrapper
          title={`M${viewingOriginProject && formattedOriginProject
            ? formattedOriginProject.id
            : viewingReopenedProject && formattedReopenedProject
              ? formattedReopenedProject.id
              : selectedProject.id
            }`}
          isOpen={projectDetailsPopup.isOpen}
          onClose={() => {
            projectDetailsPopup.closePopup();
            // Reset origin/reopened project view when closing
            handleCloseOriginProject();
            handleCloseReopenedProject();
          }}
          size="lg"
          logo={
            viewingOriginProject ? (
              <button
                onClick={handleCloseOriginProject}
                className="flex items-center justify-center w-14 h-14 hover:bg-blue-50 rounded-full transition-colors"
                title="Back to current project"
              >
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
            ) : viewingReopenedProject ? (
              <button
                onClick={handleCloseReopenedProject}
                className="flex items-center justify-center w-14 h-14 hover:bg-green-50 rounded-full transition-colors"
                title="Back to current project"
              >
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
            ) : (
              <FiFileText size={53} className="text-blue-600" />
            )
          }
          headerComponent={
            <div
              className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm ${statusColors[(
                viewingOriginProject && formattedOriginProject
                  ? formattedOriginProject.status
                  : viewingReopenedProject && formattedReopenedProject
                    ? formattedReopenedProject.status
                    : selectedProject.status
              )] || 'bg-gray-100 text-gray-800'}`}
            >
              <span className="w-4 h-4">{statusIcons[(
                viewingOriginProject && formattedOriginProject
                  ? formattedOriginProject.status
                  : viewingReopenedProject && formattedReopenedProject
                    ? formattedReopenedProject.status
                    : selectedProject.status
              )]}</span>
              <span>
                {viewingOriginProject && formattedOriginProject
                  ? (formattedOriginProject.displayStatus || formatStatusText(formattedOriginProject.status))
                  : viewingReopenedProject && formattedReopenedProject
                    ? (formattedReopenedProject.displayStatus || formatStatusText(formattedReopenedProject.status))
                    : (selectedProject.displayStatus || formatStatusText(selectedProject.status))}
              </span>
            </div>
          }
        >
          {/* Dual View Container with Sliding Animation */}
          <div className="relative overflow-hidden">
            <div
              className={`flex transition-transform duration-500 ease-in-out ${viewingOriginProject ? '-translate-x-1/2' : viewingReopenedProject ? '-translate-x-1/2' : 'translate-x-0'
                }`}
              style={{ width: viewingOriginProject || viewingReopenedProject ? '200%' : '100%' }}
            >
              {/* Current Project View */}
              <div className={`${viewingOriginProject || viewingReopenedProject ? 'w-1/2 pr-6' : 'w-full'} flex-shrink-0`}>
                {/* Current Project Header */}
                <div className="flex flex-col pb-4">
                  <div className="text-sm text-gray-500">
                    Last updated on - {selectedProject.rawProject?.projectAssigned?.[0]?.assignedDate
                      ? new Date(selectedProject.rawProject.projectAssigned[0].assignedDate).toLocaleDateString()
                      : new Date().toLocaleDateString()} (
                    {selectedProject.rawProject?.projectAssigned?.[0]?.assignedDate
                      ? new Date(selectedProject.rawProject.projectAssigned[0].assignedDate).toLocaleTimeString()
                      : new Date().toLocaleTimeString()})
                  </div>
                  {!viewingOriginProject && !viewingReopenedProject && selectedProject.rawProject?.originProject && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm font-medium">
                        <PiRepeatFill className="w-4 h-4" />
                        <span>Reopen</span>
                      </span>
                      <span className="text-sm text-gray-600">from</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('Button clicked!', selectedProject.rawProject.originProject);
                          handleViewOriginProject(selectedProject.rawProject.originProject);
                        }}
                        className="text-sm text-blue-600 font-semibold hover:text-blue-800 hover:underline transition-colors"
                      >
                        M{selectedProject.rawProject.originProject.manakProjectId}
                      </button>
                    </div>
                  )}
                  {!viewingOriginProject && !viewingReopenedProject && selectedProject.rawProject?.reopenedProjects && selectedProject.rawProject.reopenedProjects.length > 0 && selectedProject.rawProject.reopenedProjects.some((rp: any) => rp.isChargeable) && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                        <PiRepeatFill className="w-4 h-4" />
                        <span>Reopen</span>
                      </span>
                      <span className="text-sm text-gray-600">to</span>
                      {selectedProject.rawProject.reopenedProjects
                        .filter((rp: any) => rp.isChargeable)
                        .map((reopenedProj: any, index: number) => (
                          <button
                            key={reopenedProj.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log('Reopened project clicked!', reopenedProj);
                              handleViewReopenedProject(reopenedProj);
                            }}
                            className="text-sm text-blue-600 font-semibold hover:text-blue-800 hover:underline transition-colors"
                          >
                            M{reopenedProj.manakProjectId}
                            {index < selectedProject.rawProject.reopenedProjects.filter((rp: any) => rp.isChargeable).length - 1 && ', '}
                          </button>
                        ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col py-6">
                  <div className="grid grid-cols-2 gap-6">
                    <InputField
                      label="Company Name"
                      value={selectedProject.customerName}
                      className="bg-gray-100"
                      disabled
                      icon={<CiUser className="w-5 h-5" />}
                    />
                    <InputField
                      label="Article Number"
                      value={selectedProject.articleNo}
                      className="bg-gray-100"
                      disabled
                      icon={<FaRegFileAlt className="w-5 h-5" />}
                    />
                    <InputField
                      label="Project Name"
                      value={selectedProject.projectName}
                      className="bg-gray-100"
                      disabled
                      icon={<FiFileText className="w-5 h-5" />}
                    />
                    <InputField
                      label="Assigned To"
                      value={(() => {
                        // Use assignedEmployeeName directly if available from API
                        if (selectedProject.rawProject?.assignedEmployeeName) {
                          const isSelfAssigned = selectedProject.rawProject.isSelfAssigned;
                          return isSelfAssigned
                            ? `${selectedProject.rawProject.assignedEmployeeName} (Self Assigned)`
                            : selectedProject.rawProject.assignedEmployeeName;
                        }

                        // Fallback to the assignment data if directly provided name is not available
                        if (
                          selectedProject.rawProject?.projectAssigned &&
                          selectedProject.rawProject.projectAssigned.length > 0
                        ) {
                          const latestAssignment = selectedProject.rawProject.projectAssigned[0];

                          // Check if assignment has assignedToName from updated API
                          if (latestAssignment.assignedToName) {
                            return latestAssignment.adminId
                              ? `${latestAssignment.assignedToName} (Self Assigned)`
                              : latestAssignment.assignedToName;
                          }

                          // Fallback to old method of looking up employee name
                          if (latestAssignment.adminId) {
                            // Try to find admin name
                            const admin = employees.find(e => e.id === latestAssignment.adminId);
                            return admin ? `${admin.fullname} (Self Assigned)` : 'Self Assigned';
                          }

                          // Check if it's assigned to an employee
                          if (latestAssignment.employeeId) {
                            // Try to find employee name
                            const employee = employees.find(e => e.id === latestAssignment.employeeId);
                            return employee
                              ? employee.fullname
                              : `Employee #${latestAssignment.employeeId}`;
                          }
                        }

                        return 'Not specified';
                      })()}
                      className="bg-gray-100"
                      disabled
                      icon={<MdOutlineWorkOutline className="w-5 h-5" />}
                    />
                    <InputField
                      label="Received On"
                      value={new Date(
                        selectedProject.rawProject?.receivedOn ||
                        selectedProject.rawProject?.createdAt ||
                        selectedProject.createdAt
                        // Date.now()
                      ).toLocaleDateString()}
                      className="bg-gray-100"
                      disabled
                      icon={<AiOutlineClockCircle className="w-5 h-5" />}
                    />

                    {/* Render warranty fields only for BILLED projects with warranty dates */}
                    {selectedProject.status === 'Billed' && selectedProject.rawProject?.warrantyUntil ? (
                      <>
                        <InputField
                          label="Bill Number"
                          value={selectedProject.rawProject?.billNumber || 'Not specified'}
                          className="bg-gray-100"
                          disabled
                          icon={<BiReceipt className="w-5 h-5" />}
                        />
                        <div className="relative">
                          <InputField
                            label="Warranty Until"
                            value={new Date(
                              selectedProject.rawProject.warrantyUntil
                            ).toLocaleDateString()}
                            className={`bg-gray-100 ${new Date(selectedProject.rawProject.warrantyUntil) > new Date()
                              ? 'border-green-500 text-green-700'
                              : 'border-red-500 text-red-700'
                              }`}
                            disabled
                            icon={<AiOutlineClockCircle className="w-5 h-5" />}
                          />
                          <div
                            className={`absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-bold ${new Date(selectedProject.rawProject.warrantyUntil) > new Date()
                              ? 'bg-green-100 text-green-700 border border-green-500'
                              : 'bg-red-100 text-red-700 border border-red-500'
                              }`}
                            style={{ margin: 0 }}
                          >
                            {new Date(selectedProject.rawProject.warrantyUntil) > new Date()
                              ? 'Active'
                              : 'Expired'}
                          </div>
                        </div>
                        <InputField
                          label="Billed Date"
                          value={new Date(
                            selectedProject.rawProject.projectAssigned.find(
                              (a: any) => a.status === 'BILLED'
                            )?.assignedDate || selectedProject.rawProject.projectAssigned[0].assignedDate
                          ).toLocaleDateString()}
                          className="bg-gray-100"
                          disabled
                          icon={<BiReceipt className="w-5 h-5" />}
                        />
                        <div className="col-span-2">
                          <button
                            onClick={() => setShowAssignmentHistory(!showAssignmentHistory)}
                            className={`w-full px-4 py-3 rounded-lg transition-colors border-2 
                        flex items-center justify-between
                        ${showAssignmentHistory
                                ? 'bg-blue-50 border-blue-600 text-blue-700'
                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                          >
                            <div className="flex items-center gap-2">
                              <FiClock className="h-5 w-5" />
                              <span className="font-medium">View History</span>
                            </div>
                            {showAssignmentHistory ? (
                              <FiChevronUp className="h-5 w-5" />
                            ) : (
                              <FiChevronDown className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </>
                    ) : (
                      // View History Button (only shown if warranty fields are not displayed)
                      <button
                        onClick={() => setShowAssignmentHistory(!showAssignmentHistory)}
                        className={`w-full px-4 py-3 rounded-lg transition-colors border-2 
                    flex items-center justify-between
                    ${showAssignmentHistory
                            ? 'bg-blue-50 border-blue-600 text-blue-700'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        <div className="flex items-center gap-2">
                          <FiClock className="h-5 w-5" />
                          <span className="font-medium">View History</span>
                        </div>
                        {showAssignmentHistory ? (
                          <FiChevronUp className="h-5 w-5" />
                        ) : (
                          <FiChevronDown className="h-5 w-5" />
                        )}
                      </button>
                    )}
                  </div>

                  {/* Assignment History Table */}
                  <div
                    className={`mt-3 transition-all duration-300 ${showAssignmentHistory ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}
                  >
                    <TaskAssignmentTable
                      assignments={assignmentHistory}
                      visible={showAssignmentHistory}
                    />
                  </div>

                  {/* Product Attachments */}
                  <div className="py-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">Product Attachments</h3>
                    <div className="flex flex-wrap gap-4">
                      {selectedProject.rawProject?.profilePicture &&
                        selectedProject.rawProject.profilePicture.trim() !== '' &&
                        !selectedProject.rawProject.profilePicture.includes('default-project-image.png') ? (
                        <div className="relative">
                          <img
                            src={selectedProject.rawProject.profilePicture}
                            alt={`Project ${selectedProject.projectName} Image`}
                            className="w-32 h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => openImageViewer(selectedProject.rawProject.profilePicture)}
                          />
                        </div>
                      ) : selectedProject.rawProject?.projectImages?.length > 0 ? (
                        selectedProject.rawProject.projectImages.map((image, index) => (
                          <div key={index} className="relative">
                            <img
                              src={image}
                              alt={`Project Attachment ${index + 1}`}
                              className="w-32 h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => openImageViewer(image)}
                            />
                          </div>
                        ))
                      ) : (
                        <p className="text-gray-500">No attachments available</p>
                      )}
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="mt-6">
                    {(() => {
                      // Get the current project status for better error messaging
                      let currentStatus = selectedProject.status;

                      if (
                        selectedProject.rawProject?.projectAssigned &&
                        selectedProject.rawProject.projectAssigned.length > 0
                      ) {
                        const sortedAssignments = [...selectedProject.rawProject.projectAssigned].sort(
                          (a, b) =>
                            new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime() -
                            new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime()
                        );
                        if (sortedAssignments[0] && sortedAssignments[0].status) {
                          currentStatus = sortedAssignments[0].status;
                        }
                      }

                      // Normalize the status for display
                      const normalizedStatus = normalizeStatus(currentStatus);
                      const displayStatus = formatStatusText(normalizedStatus);

                      // Check if user has permission to edit this project based on its status
                      const canEdit = canEditProjectWithStatus(selectedProject);
                      const isTransferred = isProjectTransferred(selectedProject);

                      // Determine tooltip message based on why user can't edit
                      let tooltipMessage = 'Edit project';

                      if (!canEdit) {
                        if (isTransferred) {
                          tooltipMessage =
                            "You can't edit this project as it has been reassigned to another user";
                        } else {
                          tooltipMessage = `You don't have permission to edit projects with ${displayStatus} status`;
                        }
                      }

                      // Create a detailed debug message for the console
                      console.log(`Popup action button for project ${selectedProject.id || 'unknown'}:`, {
                        status: currentStatus,
                        normalizedStatus,
                        displayStatus,
                        canEdit,
                        isTransferred,
                        tooltipMessage,
                      });

                      return (
                        <button
                          onClick={() => {
                            projectDetailsPopup.closePopup();
                            handleEditProject(selectedProject);
                          }}
                          className="w-full px-4 py-4 text-lg font-semibold rounded-lg flex items-center border-2 justify-center gap-2 bg-lime-100 hover:bg-lime-200 border-lime-600 text-lime-700"
                          title="Edit project"
                        >
                          <TbEdit className="mr-1" /> Edit Project
                        </button>
                      );
                    })()}
                  </div>
                </div>
                {/* End of Current Project View */}
              </div>

              {/* Origin Project View */}
              {viewingOriginProject && formattedOriginProject && (
                <div className="w-1/2 flex-shrink-0 pl-6 border-l-2 border-gray-200">
                  {/* Origin Project Header */}
                  <div className="flex flex-col pb-4">
                    <div className="text-sm text-gray-500">
                      Last updated on - {formattedOriginProject?.rawProject?.projectAssigned?.[0]?.assignedDate
                        ? new Date(formattedOriginProject.rawProject.projectAssigned[0].assignedDate).toLocaleDateString()
                        : new Date().toLocaleDateString()} (
                      {formattedOriginProject?.rawProject?.projectAssigned?.[0]?.assignedDate
                        ? new Date(formattedOriginProject.rawProject.projectAssigned[0].assignedDate).toLocaleTimeString()
                        : new Date().toLocaleTimeString()})
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                        <span>Origin Project</span>
                      </span>
                    </div>
                  </div>

                  {originProjectLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                  ) : (
                    <div className="flex flex-col py-6">
                      <div className="grid grid-cols-2 gap-6">
                        <InputField
                          label="Company Name"
                          value={formattedOriginProject?.customerName || 'N/A'}
                          className="bg-gray-50"
                          disabled
                          icon={<CiUser className="w-5 h-5" />}
                        />
                        <InputField
                          label="Article Number"
                          value={formattedOriginProject?.articleNo || 'N/A'}
                          className="bg-gray-50"
                          disabled
                          icon={<FaRegFileAlt className="w-5 h-5" />}
                        />
                        <InputField
                          label="Project Name"
                          value={formattedOriginProject?.projectName || 'N/A'}
                          className="bg-gray-50"
                          disabled
                          icon={<FiFileText className="w-5 h-5" />}
                        />
                        <InputField
                          label="Assigned To"
                          value={(() => {
                            if (formattedOriginProject.rawProject?.assignedEmployeeName) {
                              const isSelfAssigned = formattedOriginProject.rawProject.isSelfAssigned;
                              return isSelfAssigned
                                ? `${formattedOriginProject.rawProject.assignedEmployeeName} (Self Assigned)`
                                : formattedOriginProject.rawProject.assignedEmployeeName;
                            }

                            if (
                              formattedOriginProject.rawProject?.projectAssigned &&
                              formattedOriginProject.rawProject.projectAssigned.length > 0
                            ) {
                              const latestAssignment = formattedOriginProject.rawProject.projectAssigned[0];

                              if (latestAssignment.assignedToName) {
                                return latestAssignment.adminId
                                  ? `${latestAssignment.assignedToName} (Self Assigned)`
                                  : latestAssignment.assignedToName;
                              }

                              if (latestAssignment.adminId) {
                                const admin = employees.find(e => e.id === latestAssignment.adminId);
                                return admin ? `${admin.fullname} (Self Assigned)` : 'Self Assigned';
                              }

                              if (latestAssignment.employeeId) {
                                const employee = employees.find(e => e.id === latestAssignment.employeeId);
                                return employee ? employee.fullname : `Employee #${latestAssignment.employeeId}`;
                              }
                            }

                            return 'Not specified';
                          })()}
                          className="bg-gray-100"
                          disabled
                          icon={<MdOutlineWorkOutline className="w-5 h-5" />}
                        />
                        <InputField
                          label="Received On"
                          value={new Date(
                            formattedOriginProject.rawProject?.receivedOn ||
                            formattedOriginProject.rawProject?.createdAt ||
                            Date.now()
                          ).toLocaleDateString()}
                          className="bg-gray-100"
                          disabled
                          icon={<AiOutlineClockCircle className="w-5 h-5" />}
                        />

                        {/* Render warranty fields only for BILLED projects with warranty dates */}
                        {formattedOriginProject.status === 'Billed' && formattedOriginProject.rawProject?.warrantyUntil ? (
                          <>
                            <InputField
                              label="Bill Number"
                              value={formattedOriginProject.rawProject?.billNumber || 'Not specified'}
                              className="bg-gray-100"
                              disabled
                              icon={<BiReceipt className="w-5 h-5" />}
                            />
                            <div className="relative">
                              <InputField
                                label="Warranty Until"
                                value={new Date(formattedOriginProject.rawProject.warrantyUntil).toLocaleDateString()}
                                className={`bg-gray-100 ${new Date(formattedOriginProject.rawProject.warrantyUntil) > new Date()
                                  ? 'border-green-500 text-green-700'
                                  : 'border-red-500 text-red-700'
                                  }`}
                                disabled
                                icon={<AiOutlineClockCircle className="w-5 h-5" />}
                              />
                              <div
                                className={`absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-bold ${new Date(formattedOriginProject.rawProject.warrantyUntil) > new Date()
                                  ? 'bg-green-100 text-green-700 border border-green-500'
                                  : 'bg-red-100 text-red-700 border border-red-500'
                                  }`}
                                style={{ margin: 0 }}
                              >
                                {new Date(formattedOriginProject.rawProject.warrantyUntil) > new Date() ? 'Active' : 'Expired'}
                              </div>
                            </div>
                            <InputField
                              label="Billed Date"
                              value={new Date(
                                formattedOriginProject.rawProject.projectAssigned.find(
                                  (a: any) => a.status === 'BILLED'
                                )?.assignedDate || formattedOriginProject.rawProject.projectAssigned[0].assignedDate
                              ).toLocaleDateString()}
                              className="bg-gray-100"
                              disabled
                              icon={<BiReceipt className="w-5 h-5" />}
                            />
                            <div className="col-span-2">
                              <button
                                onClick={() => setShowOriginAssignmentHistory(!showOriginAssignmentHistory)}
                                className={`w-full px-4 py-3 rounded-lg transition-colors border-2 
                                  flex items-center justify-between
                                  ${showOriginAssignmentHistory
                                    ? 'bg-blue-50 border-blue-600 text-blue-700'
                                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                  }`}
                              >
                                <div className="flex items-center gap-2">
                                  <FiClock className="h-5 w-5" />
                                  <span className="font-medium">View History</span>
                                </div>
                                {showOriginAssignmentHistory ? (
                                  <FiChevronUp className="h-5 w-5" />
                                ) : (
                                  <FiChevronDown className="h-5 w-5" />
                                )}
                              </button>
                            </div>
                          </>
                        ) : (
                          // View History Button (only shown if warranty fields are not displayed)
                          <button
                            onClick={() => setShowOriginAssignmentHistory(!showOriginAssignmentHistory)}
                            className={`w-full px-4 py-3 rounded-lg transition-colors border-2 
                              flex items-center justify-between
                              ${showOriginAssignmentHistory
                                ? 'bg-blue-50 border-blue-600 text-blue-700'
                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                          >
                            <div className="flex items-center gap-2">
                              <FiClock className="h-5 w-5" />
                              <span className="font-medium">View History</span>
                            </div>
                            {showOriginAssignmentHistory ? (
                              <FiChevronUp className="h-5 w-5" />
                            ) : (
                              <FiChevronDown className="h-5 w-5" />
                            )}
                          </button>
                        )}
                      </div>

                      {/* Assignment History Table */}
                      <div
                        className={`mt-3 transition-all duration-300 ${showOriginAssignmentHistory ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                          }`}
                      >
                        <TaskAssignmentTable
                          assignments={originAssignmentHistory}
                          visible={showOriginAssignmentHistory}
                        />
                      </div>

                      {/* Product Attachments */}
                      <div className="py-6">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">Product Attachments</h3>
                        <div className="flex flex-wrap gap-4">
                          {formattedOriginProject.rawProject?.profilePicture &&
                            formattedOriginProject.rawProject.profilePicture.trim() !== '' &&
                            !formattedOriginProject.rawProject.profilePicture.includes('default-project-image.png') ? (
                            <div className="relative">
                              <img
                                src={formattedOriginProject.rawProject.profilePicture}
                                alt={`Project ${formattedOriginProject.projectName} Image`}
                                className="w-32 h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => openImageViewer(formattedOriginProject.rawProject.profilePicture)}
                              />
                            </div>
                          ) : formattedOriginProject.rawProject?.projectImages?.length > 0 ? (
                            formattedOriginProject.rawProject.projectImages.map((image: any, index: number) => (
                              <div key={index} className="relative">
                                <img
                                  src={image}
                                  alt={`Project Attachment ${index + 1}`}
                                  className="w-32 h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => openImageViewer(image)}
                                />
                              </div>
                            ))
                          ) : (
                            <p className="text-gray-500">No attachments available</p>
                          )}
                        </div>
                      </div>

                      {/* Action Button */}
                      <div className="mt-6">
                        <button
                          onClick={() => {
                            projectDetailsPopup.closePopup();
                            handleEditProject(formattedOriginProject);
                          }}
                          className="w-full px-4 py-4 text-lg font-semibold rounded-lg flex items-center border-2 justify-center gap-2 bg-lime-100 hover:bg-lime-200 border-lime-600 text-lime-700"
                          title="Edit origin project"
                        >
                          <TbEdit className="mr-1" /> Edit Project
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Reopened Project View */}
              {viewingReopenedProject && formattedReopenedProject && (
                <div className="w-1/2 flex-shrink-0 pl-6 border-l-2 border-gray-200">
                  {/* Reopened Project Header */}
                  <div className="flex flex-col pb-4">
                    <div className="text-sm text-gray-500">
                      Last updated on - {formattedReopenedProject?.rawProject?.projectAssigned?.[0]?.assignedDate
                        ? new Date(formattedReopenedProject.rawProject.projectAssigned[0].assignedDate).toLocaleDateString()
                        : new Date().toLocaleDateString()} (
                      {formattedReopenedProject?.rawProject?.projectAssigned?.[0]?.assignedDate
                        ? new Date(formattedReopenedProject.rawProject.projectAssigned[0].assignedDate).toLocaleTimeString()
                        : new Date().toLocaleTimeString()})
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                        <span>Reopened Project</span>
                      </span>
                    </div>
                  </div>

                  {reopenedProjectLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                    </div>
                  ) : (
                    <div className="flex flex-col py-6">
                      <div className="grid grid-cols-2 gap-6">
                        <InputField
                          label="Company Name"
                          value={formattedReopenedProject?.customerName || 'N/A'}
                          className="bg-gray-50"
                          disabled
                          icon={<CiUser className="w-5 h-5" />}
                        />
                        <InputField
                          label="Article Number"
                          value={formattedReopenedProject?.articleNo || 'N/A'}
                          className="bg-gray-50"
                          disabled
                          icon={<FaRegFileAlt className="w-5 h-5" />}
                        />
                        <InputField
                          label="Project Name"
                          value={formattedReopenedProject?.projectName || 'N/A'}
                          className="bg-gray-50"
                          disabled
                          icon={<FiFileText className="w-5 h-5" />}
                        />
                        <InputField
                          label="Assigned To"
                          value={(() => {
                            if (formattedReopenedProject.rawProject?.assignedEmployeeName) {
                              const isSelfAssigned = formattedReopenedProject.rawProject.isSelfAssigned;
                              return isSelfAssigned
                                ? `${formattedReopenedProject.rawProject.assignedEmployeeName} (Self Assigned)`
                                : formattedReopenedProject.rawProject.assignedEmployeeName;
                            }

                            if (
                              formattedReopenedProject.rawProject?.projectAssigned &&
                              formattedReopenedProject.rawProject.projectAssigned.length > 0
                            ) {
                              const latestAssignment = formattedReopenedProject.rawProject.projectAssigned[0];

                              if (latestAssignment.assignedToName) {
                                return latestAssignment.adminId
                                  ? `${latestAssignment.assignedToName} (Self Assigned)`
                                  : latestAssignment.assignedToName;
                              }

                              if (latestAssignment.adminId) {
                                const admin = employees.find(e => e.id === latestAssignment.adminId);
                                return admin ? `${admin.fullname} (Self Assigned)` : 'Self Assigned';
                              }

                              if (latestAssignment.employeeId) {
                                const employee = employees.find(e => e.id === latestAssignment.employeeId);
                                return employee ? employee.fullname : `Employee #${latestAssignment.employeeId}`;
                              }
                            }

                            return 'Not specified';
                          })()}
                          className="bg-gray-100"
                          disabled
                          icon={<MdOutlineWorkOutline className="w-5 h-5" />}
                        />
                        <InputField
                          label="Received On"
                          value={new Date(
                            formattedReopenedProject.rawProject?.receivedOn ||
                            formattedReopenedProject.rawProject?.createdAt ||
                            Date.now()
                          ).toLocaleDateString()}
                          className="bg-gray-100"
                          disabled
                          icon={<AiOutlineClockCircle className="w-5 h-5" />}
                        />
                      </div>

                      {/* View History Button */}
                      <div className="flex flex-col gap-3 mt-6">
                        <button
                          onClick={() => setShowReopenedAssignmentHistory(!showReopenedAssignmentHistory)}
                          className={`w-full px-4 py-3 rounded-lg transition-colors border-2 
                            flex items-center justify-between
                            ${showReopenedAssignmentHistory
                              ? 'bg-green-50 border-green-600 text-green-700'
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                          <div className="flex items-center gap-2">
                            <FiClock className="h-5 w-5" />
                            <span className="font-medium">View History</span>
                          </div>
                          {showReopenedAssignmentHistory ? (
                            <FiChevronUp className="h-5 w-5" />
                          ) : (
                            <FiChevronDown className="h-5 w-5" />
                          )}
                        </button>
                      </div>

                      {/* Assignment History Table */}
                      <div
                        className={`mt-3 transition-all duration-300 ${showReopenedAssignmentHistory ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                          }`}
                      >
                        <TaskAssignmentTable
                          assignments={reopenedAssignmentHistory}
                          visible={showReopenedAssignmentHistory}
                        />
                      </div>

                      {/* Product Attachments */}
                      <div className="py-6">
                        <ImageViewer
                          isOpen={showReopenedProjectImages}
                          imageUrl={selectedReopenedImage}
                          onClose={() => setShowReopenedProjectImages(false)}
                        />
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-lg font-semibold text-gray-800">Product Attachments</h3>
                        </div>
                        {formattedReopenedProject.rawProject?.projectImages && formattedReopenedProject.rawProject.projectImages.length > 0 ? (
                          <div className="grid grid-cols-3 gap-4">
                            {formattedReopenedProject.rawProject.projectImages.map((image: any, index: number) => (
                              <div
                                key={index}
                                className="relative rounded-lg overflow-hidden shadow-md cursor-pointer hover:shadow-xl transition-shadow"
                                onClick={() => {
                                  setSelectedReopenedImage(image);
                                  setShowReopenedProjectImages(true);
                                }}
                              >
                                <img src={image} alt={`Product ${index + 1}`} className="w-full h-32 object-cover" />
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm">No attachments available</p>
                        )}
                      </div>

                      {/* Edit Project Button */}
                      <div className="pt-6">
                        <button
                          onClick={() => {
                            projectDetailsPopup.closePopup();
                            handleEditProject({
                              ...formattedReopenedProject,
                              rawProject: formattedReopenedProject.rawProject,
                            });
                          }}
                          className="w-full px-4 py-4 text-lg font-semibold rounded-lg flex items-center border-2 justify-center gap-2 bg-lime-100 hover:bg-lime-200 border-lime-600 text-lime-700"
                          title="Edit reopened project"
                        >
                          <TbEdit className="mr-1" /> Edit Project
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* End of Dual View Container */}
          </div>
        </PopupWrapper>
      )}

      {/* Image Viewer */}
      <ImageViewer isOpen={isImageViewerOpen} imageUrl={viewerImage} onClose={closeImageViewer} />
    </React.Fragment>
  );
};

export { AllProjects as Projects };
export default AllProjects;
